import {
  COLLECTION_TYPES,
  DEFAULT_BREAKFAST_LIMIT,
  FIXED_OPTIONS,
  STAGE_IDS,
  STEP_IDS,
  createInitialState,
  getCollectionTotal
} from '@theme/meal-plan-builder-state';
import {
  VEGAN_TAG,
  VEGETARIAN_TAG,
  cardHasTag,
  createCollectionCache,
  fetchCollectionHtml,
  getCardProductId,
  getCollectionTypeFromElement,
  getProductCards,
  mergeCollectionHtmlByProduct,
  moveTaggedCardsToEnd,
  setCardQuantity,
  syncCollectionCardNutrition,
  syncCollectionCardQuantities,
  updateCollectionIncrementButtons
} from '@theme/meal-plan-builder-collections';
import {
  isBreakfastStageComplete,
  isDrinksStageComplete,
  isMealsStageComplete,
  isStep1Complete
} from '@theme/meal-plan-builder-stepper';
import {
  fetchDiscountDetails,
  isValidEmail,
  submitSignup,
  reportError,
  reportMessage
} from '@theme/meal-plan-builder-api';
import {
  loadEmail,
  loadPostcode,
  loadStep1,
  loadStep3,
  saveEmail,
  savePostcode,
  saveStep1,
  saveStep3
} from '@theme/meal-plan-builder-storage';
import { buildRechargeCartPayload } from '@theme/meal-plan-builder-cart';

const FILTER_CONTROL_IDS = {
  DIET: 'diet',
  PORTION: 'portion',
  MEALS: 'meals',
  BREAKFAST: 'breakfast',
  DELIVERY_DAY: 'delivery-day'
};
const VEGAN_HIDDEN_PORTIONS = ['large', 'lean'];
const DEFAULT_PORTION = 'standard';

const MARKET_CONFIG = {
  IE: {
    shortName: 'Ireland',
    fullName: 'Ireland',
    url: 'https://ie.simmereats.com/collections/all'
  },
  UK: {
    shortName: 'UK',
    fullName: 'United Kingdom',
    url: 'https://simmereats.com/join-now/plans'
  },
  NI: {
    shortName: 'Northern Ireland',
    fullName: 'Northern Ireland',
    url: 'https://uk.simmereats.com/collections/all'
  }
};

const DEFAULT_STORE_MARKET = 'ROI';

class MealPlanBuilder extends HTMLElement {
  constructor() {
    super();
    this.collectionCache = createCollectionCache();
    this.renderedHandles = {
      [COLLECTION_TYPES.MEALS]: '',
      [COLLECTION_TYPES.BREAKFAST]: '',
      [COLLECTION_TYPES.DRINKS]: ''
    };
    this.loadingRequestId = 0;
    this.isSubmittingContact = false;
    this.isSubmittingCheckout = false;
    this.desktopOpenControl = null;
    this.mobileFilterDraft = null;
    this.mobileFilterAccordionState = this.createEmptyMobileFilterAccordionState();
    this.step1Discount = {
      ok: false,
      code: '',
      type: '',
      value: 0
    };
    this.moneyFormatter = null;
    this.step3PreloadPromise = null;
    this.pendingSessionRevalidation = false;
    this.rechargeInitPromise = null;
    this.stepperCollectionTotal = this.getCollectionTotal.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.deliveryDayOptions = [];
    this.selectedDeliveryDayKey = '';
    this.pendingMarketRedirect = null;
    this.contactSubmitDefaultText = null;
  }

  connectedCallback() {
    this.initializeConfig();
    this.initializeRecharge().catch((error) => {
      console.warn('Recharge SDK failed to initialize:', error);
    });
    this.initializeState();
    this.cacheElements();
    this.renderDeliveryDayControlOptions();

    this.setupEventListeners();
    this.hydrateFromSession();
    this.syncInputsFromState();
    this.ensureValidState();
    this.updateStep1MealOptionPrices();
    this.updateBreakfastOptionPrices();
    this.updateShotPackOptionPrices();
    this.applyStepUI();
    this.applyStageUI();
    this.updateActionStates();
    this.updateFooter();
    this.syncFilterControlsUI();
    this.updateStep1PricingUI();
    this.loadStep1DiscountDetails();
    this.preloadStep3CollectionsFromStep1();
    this.checkAndRedirectIfActiveSubscriber();

    if (this.state.step === STEP_IDS.BUILD) {
      this.ensureCollectionsLoaded();
    }
  }

  initializeRecharge() {
    if (this.rechargeInitPromise) {
      return this.rechargeInitPromise;
    }

    this.rechargeInitPromise = this.waitForRechargeClient()
      .then((rechargeClient) => {
        rechargeClient.init({
          storeIdentifier: window.Shopify.shop,
          storefrontAccessToken: 'strfnt_5b6d2de7e96c8bb81b3f24046331bc872711694ecbc74b5689ea9fd3693c3e8b',
          appName: 'Meal Plan Builder',
          appVersion: '1.0.0',
          loginRetryFn: () => rechargeClient.auth.loginShopifyAppProxy().then((session) => session)
        });

        return rechargeClient;
      })
      .catch((error) => {
        this.rechargeInitPromise = null;
        throw error;
      });

    return this.rechargeInitPromise;
  }

  waitForRechargeClient(timeoutMs = 10000) {
    if (window.recharge?.init) {
      return Promise.resolve(window.recharge);
    }

    return new Promise((resolve, reject) => {
      const startedAt = Date.now();

      const checkForRecharge = () => {
        if (window.recharge?.init) {
          resolve(window.recharge);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          reject(new Error('Recharge bundle client failed to load.'));
          return;
        }

        window.setTimeout(checkForRecharge, 50);
      };

      checkForRecharge();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  async checkAndRedirectIfActiveSubscriber() {
    const email = loadEmail();
    if (!email) return;

    // No postcode: this is a lookup only, so nothing is written to Klaviyo.
    const { customer } = await submitSignup({ email });

    if (customer.isExistingSubscriber) {
      window.location.assign('/pages/bundle-manager');
    }
  }

  initializeConfig() {
    this.config = this.parseJSONScript('config', {
      diets: {},
      drinksHandle: 'drinks'
    });

    this.bundleMeta = this.parseJSONScript('bundle-product', {
      product: null,
      sellingPlans: {}
    });

    this.pricingReferenceMeta = this.parseJSONScript('pricing-reference-product', {
      product: null
    });
    this.pricingReferenceLookup = this.buildPricingReferenceLookup(this.pricingReferenceMeta.product);

    this.breakfastPricingReferenceMeta = this.parseJSONScript('breakfast-pricing-reference-product', {
      product: null
    });
    this.breakfastPricingReferenceLookup = this.buildBreakfastPricingReferenceLookup(this.breakfastPricingReferenceMeta.product);

    const deliveryDaysConfig = this.parseJSONScript('delivery-days', null);
    this.deliveryDayOptions = this.buildDeliveryDayOptions(deliveryDaysConfig);
    this.selectedDeliveryDayKey = this.getDefaultSelectedDeliveryDayKey();
  }

  initializeState() {
    const customerLoggedIn = this.dataset.isLoggedIn === 'true';
    const showDrinks = this.dataset.showDrinks === 'true';

    this.state = createInitialState({
      customerLoggedIn,
      showDrinks
    });

    this.state.step2.completed = customerLoggedIn || Boolean(loadEmail());

    if (!showDrinks) {
      this.state.step3.drinksDecision = 'skip';
      this.state.step3.limits[COLLECTION_TYPES.DRINKS] = 0;
    }
  }

  cacheElements() {
    this.elements = {
      stepPanels: this.querySelectorAll('[js-meal-plan-builder="step-panel"][data-step]'),
      stagePanels: this.querySelectorAll('[js-meal-plan-builder="stage-panel"][data-stage]'),
      stageIndicators: this.querySelectorAll('[js-meal-plan-builder="stage-indicator"]'),
      desktopFilterControls: this.querySelectorAll('[js-meal-plan-builder="desktop-filter-control"]'),
      desktopFilterMenus: this.querySelectorAll('[js-meal-plan-builder="desktop-filter-menu"]'),
      controlValueLabels: this.querySelectorAll('[js-meal-plan-builder="control-value"]'),
      mobileFilterOverlay: this.querySelector('[js-meal-plan-builder="mobile-filter-overlay"]'),
      mobileFilterPanel: this.querySelector('[js-meal-plan-builder="mobile-filter-panel"]'),
      mobileFilterAccordions: this.querySelectorAll('[js-meal-plan-builder="mobile-filter-accordion"]'),
      selectPlanButton: this.querySelector('[js-meal-plan-builder="select-plan"]'),
      contactForm: this.querySelector('[js-meal-plan-builder="contact-form"]'),
      contactSubmit: this.querySelector('[js-meal-plan-builder="contact-submit"]'),
      contactError: this.querySelector('[js-meal-plan-builder="contact-error"]'),
      globalError: this.querySelector('[js-meal-plan-builder="global-error"]'),
      drinksDecisionButtons: this.querySelectorAll('[js-meal-plan-builder="drinks-decision"]'),
      shotPackInputs: this.querySelectorAll('[js-meal-plan-builder="shot-pack"]'),
      drinksSubtitle: this.querySelector('[js-meal-plan-builder="drinks-subtitle"]'),
      shotBadge: this.querySelector('[js-meal-plan-builder="shot-badge"]'),
      shotBadgePrice: this.querySelector('[js-meal-plan-builder="shot-badge-price"]'),
      footerSummary: this.querySelector('[js-meal-plan-builder="footer-summary"]'),
      summaryActionButton: this.querySelector('[js-meal-plan-builder="footer-action"]'),
      step1Pricing: this.querySelector('[js-meal-plan-builder="step1-pricing"]'),
      step1PricePerMeal: this.querySelector('.meal-plan-builder__step1-price-per-meal'),
      step1PricePerMealOld: this.querySelector('[js-meal-plan-builder="step1-price-per-meal-old"]'),
      step1PricePerMealNew: this.querySelector('[js-meal-plan-builder="step1-price-per-meal-new"]'),
      step1MainMealsCount: this.querySelector('[js-meal-plan-builder="step1-main-meals-count"]'),
      step1MainMealsTotal: this.querySelector('[js-meal-plan-builder="step1-main-meals-total"]'),
      step1MainMealsTotalOld: this.querySelector('[js-meal-plan-builder="step1-main-meals-total-old"]'),
      step1PortionGrid: this.querySelector('[js-meal-plan-builder="step1-portion-grid"]'),
      step1PortionSizeContainer: this.querySelectorAll('[js-meal-plan-builder="step1-portion-size-container"]'),
      step1PortionFootnotes: this.querySelector('[js-meal-plan-builder="step1-portion-footnotes"]'),
      step1PortionFootnoteItems: this.querySelectorAll('[js-meal-plan-builder="step1-portion-footnote"]'),
      step1DiscountRow: this.querySelector('[js-meal-plan-builder="step1-discount-row"]'),
      step1DiscountBadge: this.querySelector('[js-meal-plan-builder="step1-discount-badge"]'),
      step1TotalOld: this.querySelector('[js-meal-plan-builder="step1-total-old"]'),
      step1TotalNew: this.querySelector('[js-meal-plan-builder="step1-total-new"]'),
      collectionContents: {
        [COLLECTION_TYPES.MEALS]: this.querySelector('[js-meal-plan-builder="collection-content"][data-collection="meals"]'),
        [COLLECTION_TYPES.BREAKFAST]: this.querySelector('[js-meal-plan-builder="collection-content"][data-collection="breakfast"]'),
        [COLLECTION_TYPES.DRINKS]: this.querySelector('[js-meal-plan-builder="collection-content"][data-collection="drinks"]')
      },
      collectionRoots: {
        [COLLECTION_TYPES.MEALS]: this.querySelector('[data-collection-section="meals"]'),
        [COLLECTION_TYPES.BREAKFAST]: this.querySelector('[data-collection-section="breakfast"]'),
        [COLLECTION_TYPES.DRINKS]: this.querySelector('[data-collection-section="drinks"]')
      },
      step1Inputs: {
        diet: this.querySelectorAll('[js-meal-plan-builder="step1-diet"]'),
        portion: this.querySelectorAll('[js-meal-plan-builder="step1-portion"]'),
        meals: this.querySelectorAll('[js-meal-plan-builder="step1-meals"]')
      },
      controls: {
        diet: this.querySelector('[js-meal-plan-builder="control-diet"]'),
        portion: this.querySelector('[js-meal-plan-builder="control-portion"]'),
        meals: this.querySelector('[js-meal-plan-builder="control-meals"]'),
        breakfast: this.querySelector('[js-meal-plan-builder="control-breakfast"]'),
        deliveryDay: this.querySelector('[js-meal-plan-builder="control-delivery-day"]')
      },
      breakfastLimitInputs: this.querySelectorAll('[js-meal-plan-builder="breakfast-limit"]'),
      desktopDeliveryDayOptionsContainer: this.querySelector('[js-meal-plan-builder-delivery-day="desktop-options"]'),
      mobileDeliveryDayOptionsContainer: this.querySelector('[js-meal-plan-builder-delivery-day="mobile-options"]'),
      desktopDeliveryDayOptionTemplate: this.querySelector('[js-meal-plan-builder-delivery-day="desktop-option-template"]'),
      mobileDeliveryDayOptionTemplate: this.querySelector('[js-meal-plan-builder-delivery-day="mobile-option-template"]')
    };
  }

  setupEventListeners() {
    this.addEventListener('change', this.handleChange.bind(this));
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('submit', this.handleSubmit.bind(this));
    this.addEventListener('input', this.handleInput.bind(this));
    this.addEventListener('productQuantityChanged', this.handleProductQuantityChanged.bind(this));
    document.addEventListener('click', this.handleDocumentClick);
  }

  hydrateFromSession() {
    const savedStep1 = loadStep1();

    if (savedStep1) {
      if (FIXED_OPTIONS.DIETS.includes(savedStep1.diet)) {
        this.state.step1.diet = savedStep1.diet;
      }

      if (FIXED_OPTIONS.PORTIONS.includes(savedStep1.portion)) {
        this.state.step1.portion = savedStep1.portion;
      }

      if (this.isValidMealsPerWeekOption(savedStep1.mealsPerWeek)) {
        this.state.step1.mealsPerWeek = savedStep1.mealsPerWeek;
      }

      this.enforcePortionDietRules();
    }

    this.state.step3.limits[COLLECTION_TYPES.MEALS] = this.state.step1.mealsPerWeek || 0;

    const savedStep3 = loadStep3();

    if (!savedStep3) {
      return;
    }

    const canResumePastStep1 = savedStep1 && isStep1Complete(savedStep1);

    if (canResumePastStep1) {
      this.state.step = this.shouldSkipContactStep() ? STEP_IDS.BUILD : STEP_IDS.CONTACT;
    }

    const hydratedMealsLimit = savedStep3.limits[COLLECTION_TYPES.MEALS];

    if (this.isValidMealsPerWeekOption(hydratedMealsLimit)) {
      this.state.step1.mealsPerWeek = hydratedMealsLimit;
      this.state.step3.limits[COLLECTION_TYPES.MEALS] = hydratedMealsLimit;
    }

    this.state.step3.collections = savedStep3.collections;

    // Saved selections are keyed by product ID. Because the menu rotates each
    // week, those IDs can point at products that are no longer available, so we
    // must revalidate the restored selections once the current week's
    // collections have actually loaded.
    this.pendingSessionRevalidation = true;

    if (savedStep3.breakfastSkipped || savedStep3.breakfastLimit > 0) {
      this.state.step3.breakfastLimit = savedStep3.breakfastLimit;
      this.state.step3.breakfastSkipped = savedStep3.breakfastSkipped;
      this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] = savedStep3.limits[COLLECTION_TYPES.BREAKFAST];
    }
    this.state.step3.drinksDecision = savedStep3.drinksDecision;

    if (this.state.showDrinks) {
      this.state.step3.limits[COLLECTION_TYPES.DRINKS] = savedStep3.limits[COLLECTION_TYPES.DRINKS] || 1;
    } else {
      this.state.step3.drinksDecision = 'skip';
      this.state.step3.limits[COLLECTION_TYPES.DRINKS] = 0;
    }

    this.enforceBreakfastDietRules();

    if (this.state.step !== STEP_IDS.BUILD) {
      return;
    }

    const stageFromSession = savedStep3.stage;
    const requestedStage = Object.values(STAGE_IDS).includes(stageFromSession) ? stageFromSession : STAGE_IDS.MEALS;

    this.state.stage = this.getNearestAccessibleStage(requestedStage, this.getStageCompletionState());
  }

  ensureValidState() {
    this.enforcePortionDietRules();
    this.enforceBreakfastDietRules();

    this.clampCollectionToLimit(COLLECTION_TYPES.MEALS);
    this.clampCollectionToLimit(COLLECTION_TYPES.BREAKFAST);
    this.clampCollectionToLimit(COLLECTION_TYPES.DRINKS);

    if (!this.state.showDrinks) {
      this.state.step3.drinksDecision = 'skip';
      this.state.step3.limits[COLLECTION_TYPES.DRINKS] = 0;

      if (this.state.stage === STAGE_IDS.DRINKS) {
        this.state.stage = this.isBreakfastDisabledForDiet() ? STAGE_IDS.MEALS : STAGE_IDS.BREAKFAST;
      }
    }
  }

  parseJSONScript(key, fallback) {
    const script = this.querySelector(`[js-meal-plan-builder="${key}"]`);

    if (!script) {
      return fallback;
    }

    try {
      return JSON.parse(script.textContent.trim());
    } catch (error) {
      console.warn(`Failed to parse "${key}" JSON payload:`, error);
      return fallback;
    }
  }

  parseJsonString(value, fallback = null) {
    try {
      return JSON.parse(String(value || ''));
    } catch (error) {
      return fallback;
    }
  }

  normalizeDeliveryDay(value) {
    const normalizedValue = String(value || '').trim().toLowerCase();
    const allowedDays = new Set(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']);

    if (!allowedDays.has(normalizedValue)) {
      const matchedDay = [...allowedDays].find((dayKey) => {
        return new RegExp(`\\b${dayKey}\\b`, 'i').test(normalizedValue);
      });

      return matchedDay || '';
    }

    return normalizedValue;
  }

  getDeliveryDayKeyFromWeekDay(value) {
    const parsedWeekDay = Number(value);

    if (!Number.isFinite(parsedWeekDay)) {
      return '';
    }

    const normalizedWeekDay = Math.trunc(parsedWeekDay);
    const dayKeyByWeekDay = {
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday',
      7: 'sunday'
    };

    return dayKeyByWeekDay[normalizedWeekDay] || '';
  }

  getDeliveryDayKeyFromConfigValue(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const weekDayKey = this.getDeliveryDayKeyFromWeekDay(value.week_day ?? value.weekDay ?? value.day_of_week ?? value.dayOfWeek);

      if (weekDayKey) {
        return weekDayKey;
      }

      const namedDay = value.delivery_day ?? value.deliveryDay ?? value.day ?? value.name;
      return this.normalizeDeliveryDay(namedDay);
    }

    return this.normalizeDeliveryDay(value);
  }

  normalizeDeliveryDaysValue(value) {
    let rawDeliveryDays = [];

    if (Array.isArray(value)) {
      rawDeliveryDays = value;
    } else if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return [];
      }

      const parsedStringValue = this.parseJsonString(trimmedValue, null);

      if (Array.isArray(parsedStringValue)) {
        rawDeliveryDays = parsedStringValue;
      } else if (parsedStringValue && typeof parsedStringValue === 'object') {
        if (Array.isArray(parsedStringValue.days)) {
          rawDeliveryDays = parsedStringValue.days;
        } else if (Array.isArray(parsedStringValue.delivery_days)) {
          rawDeliveryDays = parsedStringValue.delivery_days;
        } else if (this.getDeliveryDayKeyFromConfigValue(parsedStringValue)) {
          rawDeliveryDays = [parsedStringValue];
        } else {
          rawDeliveryDays = Object.values(parsedStringValue);
        }
      } else {
        rawDeliveryDays = trimmedValue.split(',');
      }
    } else if (value && typeof value === 'object') {
      if (Array.isArray(value.days)) {
        rawDeliveryDays = value.days;
      } else if (Array.isArray(value.delivery_days)) {
        rawDeliveryDays = value.delivery_days;
      } else if (this.getDeliveryDayKeyFromConfigValue(value)) {
        rawDeliveryDays = [value];
      } else {
        rawDeliveryDays = Object.values(value);
      }
    }

    const normalizedUniqueDays = [];
    const seenDays = new Set();

    rawDeliveryDays.forEach((dayValue) => {
      const normalizedDay = this.getDeliveryDayKeyFromConfigValue(dayValue);

      if (!normalizedDay || seenDays.has(normalizedDay)) {
        return;
      }

      seenDays.add(normalizedDay);
      normalizedUniqueDays.push(normalizedDay);
    });

    return normalizedUniqueDays;
  }

  shouldForceNextMondayDeliveryDay() {
    const normalizedValue = String(this.dataset.forceNextMondayDeliveryDay || '').trim().toLowerCase();

    if (!normalizedValue) {
      return true;
    }

    if (normalizedValue === 'false' || normalizedValue === '0' || normalizedValue === 'no' || normalizedValue === 'off') {
      return false;
    }

    return normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'yes' || normalizedValue === 'on';
  }

  getNextMondayDeliveryDate(referenceDate = new Date()) {
    const mondayWeekday = 1;
    const cutoffWeekday = 2;
    const cutoffHourUtc = 12;
    const deliveryDate = new Date(Date.UTC(
      referenceDate.getUTCFullYear(),
      referenceDate.getUTCMonth(),
      referenceDate.getUTCDate()
    ));
    let dayOffset = mondayWeekday - deliveryDate.getUTCDay();

    if (dayOffset <= 0) {
      dayOffset += 7;
    }

    if (
      referenceDate.getUTCDay() > cutoffWeekday ||
      (referenceDate.getUTCDay() === cutoffWeekday && referenceDate.getUTCHours() >= cutoffHourUtc)
    ) {
      dayOffset += 7;
    }

    deliveryDate.setDate(deliveryDate.getDate() + dayOffset);
    return this.applyDeliveryDateHolidayOverride(deliveryDate);
  }

  applyDeliveryDateHolidayOverride(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return date;
    }

    // Ireland August bank holiday: Monday 3 Aug 2026 delivery moves to Saturday 1 Aug 2026.
    if (date.getFullYear() === 2026 && date.getMonth() === 7 && date.getDate() === 3) {
      return new Date(2026, 7, 1, 4);
    }

    return date;
  }

  getNextMondayDeliveryDayOption() {
    const deliveryDate = this.getNextMondayDeliveryDate();

    return {
      key: 'monday',
      dayLabel: 'Monday',
      deliveryDate: this.toIsoDateString(deliveryDate),
      label: this.formatDeliveryDayOptionLabel('monday', deliveryDate)
    };
  }

  getUpcomingDateForDeliveryDay(dayKey) {
    const jsWeekdayByKey = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6
    };
    const targetDay = jsWeekdayByKey[dayKey];

    if (!Number.isFinite(targetDay)) {
      return null;
    }

    const now = new Date();
    const referenceDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    let dayOffset = targetDay - referenceDate.getUTCDay();

    if (dayOffset < 0) {
      dayOffset += 7;
    }

    referenceDate.setUTCDate(referenceDate.getUTCDate() + dayOffset);
    return referenceDate;
  }

  toIsoDateString(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  getOrdinalSuffix(day) {
    const lastTwoDigits = day % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return 'th';
    }

    const lastDigit = day % 10;

    if (lastDigit === 1) {
      return 'st';
    }

    if (lastDigit === 2) {
      return 'nd';
    }

    if (lastDigit === 3) {
      return 'rd';
    }

    return 'th';
  }

  formatDeliveryDayOptionLabel(dayKey, dateValue) {
    const normalizedDay = this.normalizeDeliveryDay(dayKey);
    const deliveryDate = dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue
      : this.getUpcomingDateForDeliveryDay(normalizedDay);

    if (!(deliveryDate instanceof Date) || Number.isNaN(deliveryDate.getTime())) {
      return normalizedDay;
    }

    const day = deliveryDate.getUTCDate();
    const weekdayLabel = deliveryDate.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' });
    const monthLabel = deliveryDate.toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' });

    return `${weekdayLabel} ${monthLabel} ${day}${this.getOrdinalSuffix(day)}`;
  }

  buildDeliveryDayOptions(value) {
    if (this.shouldForceNextMondayDeliveryDay()) {
      return [this.getNextMondayDeliveryDayOption()];
    }

    const deliveryDayKeys = this.normalizeDeliveryDaysValue(value);

    if (!deliveryDayKeys.length) {
      return [];
    }

    return deliveryDayKeys
      .map((dayKey) => {
        const upcomingDate = this.getUpcomingDateForDeliveryDay(dayKey);

        if (!(upcomingDate instanceof Date) || Number.isNaN(upcomingDate.getTime())) {
          return null;
        }

        return {
          key: dayKey,
          dayLabel: upcomingDate.toLocaleDateString('en-GB', { weekday: 'long' }),
          deliveryDate: this.toIsoDateString(upcomingDate),
          label: this.formatDeliveryDayOptionLabel(dayKey, upcomingDate),
          sortTimestamp: upcomingDate.getTime()
        };
      })
      .filter(Boolean)
      .sort((leftOption, rightOption) => {
        return leftOption.sortTimestamp - rightOption.sortTimestamp;
      })
      .map(({ sortTimestamp, ...option }) => option);
  }

  getDefaultSelectedDeliveryDayKey() {
    return this.deliveryDayOptions[0]?.key || '';
  }

  getDeliveryDayOptionByKey(key) {
    const normalizedKey = this.normalizeDeliveryDay(key);
    return this.deliveryDayOptions.find((option) => option.key === normalizedKey) || null;
  }

  getSelectedDeliveryDayKey() {
    const currentSelection = this.getDeliveryDayOptionByKey(this.selectedDeliveryDayKey);

    if (currentSelection) {
      return currentSelection.key;
    }

    return this.getDefaultSelectedDeliveryDayKey();
  }

  hasMultipleDeliveryDayOptions() {
    return this.deliveryDayOptions.length > 1;
  }

  setSelectedDeliveryDayKey(key, { shouldSyncUI = true } = {}) {
    const selectedOption = this.getDeliveryDayOptionByKey(key) || this.getDeliveryDayOptionByKey(this.getDefaultSelectedDeliveryDayKey());
    const nextSelectedKey = selectedOption?.key || '';

    if (!nextSelectedKey) {
      return;
    }

    this.selectedDeliveryDayKey = nextSelectedKey;

    if (shouldSyncUI) {
      this.syncBuildControlsFromState();
    }
  }

  renderDeliveryDayControlOptions() {
    const options = this.deliveryDayOptions;
    const selectedDeliveryDayKey = this.getSelectedDeliveryDayKey() || options[0]?.key || '';

    this.selectedDeliveryDayKey = selectedDeliveryDayKey;

    const renderDesktopOptions = () => {
      const container = this.elements.desktopDeliveryDayOptionsContainer;
      const template = this.elements.desktopDeliveryDayOptionTemplate;

      if (!container || !template) {
        return;
      }

      const fragment = document.createDocumentFragment();

      options.forEach((option) => {
        const templateContent = template.content.firstElementChild?.cloneNode(true);

        if (!(templateContent instanceof HTMLElement)) {
          return;
        }

        templateContent.dataset.value = option.key;
        const labelElement = templateContent.querySelector('[js-meal-plan-builder-delivery-day="option-label"]');

        if (labelElement) {
          labelElement.textContent = option.label;
        }

        fragment.appendChild(templateContent);
      });

      container.querySelectorAll('[js-meal-plan-builder="desktop-filter-option"][data-control="delivery-day"]').forEach((optionNode) => {
        optionNode.remove();
      });
      container.appendChild(fragment);
    };

    const renderMobileOptions = () => {
      const container = this.elements.mobileDeliveryDayOptionsContainer;
      const template = this.elements.mobileDeliveryDayOptionTemplate;

      if (!container || !template) {
        return;
      }

      const fragment = document.createDocumentFragment();

      options.forEach((option) => {
        const templateContent = template.content.firstElementChild?.cloneNode(true);

        if (!(templateContent instanceof HTMLElement)) {
          return;
        }

        templateContent.dataset.value = option.key;
        const labelElement = templateContent.querySelector('[js-meal-plan-builder-delivery-day="option-label"]');

        if (labelElement) {
          labelElement.textContent = option.label;
        }

        fragment.appendChild(templateContent);
      });

      container.querySelectorAll('[js-meal-plan-builder="mobile-filter-option"][data-control="delivery-day"]').forEach((optionNode) => {
        optionNode.remove();
      });
      container.appendChild(fragment);
    };

    renderDesktopOptions();
    renderMobileOptions();

    if (this.elements.controls.deliveryDay) {
      const selectElement = this.elements.controls.deliveryDay;

      while (selectElement.firstChild) {
        selectElement.removeChild(selectElement.firstChild);
      }

      options.forEach((option) => {
        const optionElement = document.createElement('option');
        optionElement.value = option.key;
        optionElement.textContent = option.label;
        selectElement.appendChild(optionElement);
      });

      selectElement.value = selectedDeliveryDayKey;
    }
  }

  canInteractWithFilterControl(control) {
    if (control === FILTER_CONTROL_IDS.DELIVERY_DAY) {
      return this.hasMultipleDeliveryDayOptions();
    }

    if (control === FILTER_CONTROL_IDS.PORTION) {
      return !this.isSinglePortionDiet(this.mobileFilterDraft?.diet);
    }

    return true;
  }

  getMealsPerWeekOptions() {
    return this.getUniquePositiveNumbersFromInputs(this.elements.step1Inputs.meals);
  }

  getBreakfastPerWeekOptions() {
    return this.getUniquePositiveNumbersFromInputs(this.elements.breakfastLimitInputs);
  }

  getUniquePositiveNumbersFromInputs(inputs) {
    const values = Array.from(inputs || [])
      .map((input) => Number(input.value))
      .filter((value) => Number.isFinite(value) && value > 0);

    return [...new Set(values)];
  }

  isValidMealsPerWeekOption(value) {
    return this.getMealsPerWeekOptions().includes(Number(value));
  }

  isValidBreakfastPerWeekOption(value) {
    return this.getBreakfastPerWeekOptions().includes(Number(value));
  }

  getDefaultMealsPerWeekOption() {
    return this.getMealsPerWeekOptions()[0] || 0;
  }

  createEmptyMobileFilterAccordionState() {
    return {
      [FILTER_CONTROL_IDS.DIET]: false,
      [FILTER_CONTROL_IDS.PORTION]: false,
      [FILTER_CONTROL_IDS.MEALS]: false,
      [FILTER_CONTROL_IDS.BREAKFAST]: false,
      [FILTER_CONTROL_IDS.DELIVERY_DAY]: false
    };
  }

  isValidFilterControl(control) {
    return Object.values(FILTER_CONTROL_IDS).includes(control);
  }

  getCheckedInput(inputs) {
    return Array.from(inputs || []).find((input) => input.checked) || null;
  }

  handleChange(event) {
    const target = event.target;

    if (target.matches('[js-meal-plan-builder="step1-diet"]')) {
      this.updateStep1({
        diet: target.value
      });

      return;
    }

    if (target.matches('[js-meal-plan-builder="step1-portion"]')) {
      this.updateStep1({
        portion: target.value
      });

      return;
    }

    if (target.matches('[js-meal-plan-builder="step1-meals"]')) {
      this.updateStep1({
        mealsPerWeek: Number(target.value)
      });

      return;
    }

    if (target.matches('[js-meal-plan-builder="control-diet"]')) {
      this.updateStep1({ diet: target.value }, { fromBuildControls: true });
      return;
    }

    if (target.matches('[js-meal-plan-builder="control-portion"]')) {
      this.updateStep1({ portion: target.value }, { fromBuildControls: true });
      return;
    }

    if (target.matches('[js-meal-plan-builder="control-meals"]')) {
      this.updateStep1({ mealsPerWeek: Number(target.value) }, { fromBuildControls: true });
      return;
    }

    if (target.matches('[js-meal-plan-builder="control-breakfast"]')) {
      this.updateBreakfastLimitFromControl(target.value);
      return;
    }

    if (target.matches('[js-meal-plan-builder="control-delivery-day"]')) {
      this.applyControlValue(FILTER_CONTROL_IDS.DELIVERY_DAY, target.value);
      return;
    }

    if (target.matches('[js-meal-plan-builder="breakfast-limit"]')) {
      this.setBreakfastLimit(Number(target.value));
      return;
    }

    if (target.matches('[js-meal-plan-builder="shot-pack"]')) {
      this.setShotPackQuantity(Number(target.value));
    }
  }

  handleClick(event) {
    const desktopFilterToggle = event.target.closest('[js-meal-plan-builder="desktop-filter-toggle"]');

    if (desktopFilterToggle && this.contains(desktopFilterToggle)) {
      this.toggleDesktopControl(desktopFilterToggle.dataset.control);
      return;
    }

    const mobileFilterToggle = event.target.closest('[js-meal-plan-builder="mobile-filter-toggle"]');

    if (mobileFilterToggle && this.contains(mobileFilterToggle)) {
      this.toggleMobileAccordion(mobileFilterToggle.dataset.control);
      return;
    }

    const actionTarget = event.target.closest('[js-meal-plan-builder]');

    if (!actionTarget) {
      this.setDesktopOpenControl(null);
      return;
    }

    const action = actionTarget.getAttribute('js-meal-plan-builder');

    if (!['desktop-filter-toggle', 'desktop-filter-option'].includes(action)) {
      this.setDesktopOpenControl(null);
    }

    if (action === 'select-plan') {
      this.handleSelectPlan();
      return;
    }

    if (action === 'footer-action') {
      this.handleFooterAction();
      return;
    }

    if (action === 'breakfast-skip') {
      this.skipBreakfast({ autoAdvance: true });
      return;
    }

    if (action === 'drinks-decision') {
      const decision = actionTarget.dataset.value;

      this.setDrinksDecision(decision);

      if (decision === 'skip') {
        this.handleCheckout();
      }

      return;
    }

    if (action === 'stage-indicator') {
      this.handleStageIndicatorClick(actionTarget.dataset.stage);
      return;
    }

    if (action === 'desktop-filter-option') {
      this.handleDesktopControlOption(actionTarget.dataset.control, actionTarget.dataset.value);
      return;
    }

    if (action === 'open-mobile-filters') {
      this.openMobileFilters();
      return;
    }

    if (action === 'close-mobile-filters') {
      this.closeMobileFilters();
      return;
    }

    if (action === 'mobile-filter-option') {
      this.handleMobileControlOption(actionTarget.dataset.control, actionTarget.dataset.value);
      return;
    }

    if (action === 'apply-mobile-filters') {
      this.applyMobileFilterDraft();
      return;
    }

    if (action === 'mobile-filter-overlay') {
      if (event.target === actionTarget) {
        this.closeMobileFilters();
      }

      return;
    }

    if (action === 'mobile-filter-panel') {
      return;
    }

  }

  handleSubmit(event) {
    const form = event.target.closest('[js-meal-plan-builder="contact-form"]');

    if (!form) {
      return;
    }

    event.preventDefault();
    this.handleContactSubmit();
  }

  handleInput(event) {
    if (event.target.closest('[js-meal-plan-builder="contact-form"]')) {
      this.clearMarketMismatch();
    }
  }

  handleDocumentClick(event) {
    const filterControl = event.target.closest('[js-meal-plan-builder="desktop-filter-control"]');

    if (!filterControl || !this.contains(filterControl)) {
      this.setDesktopOpenControl(null);
    }
  }

  handleProductQuantityChanged(event) {
    const productCard = event.detail?.productCard || event.target.closest('product-card');

    if (!productCard) {
      return;
    }

    const collectionType = getCollectionTypeFromElement(productCard);

    if (!collectionType || !this.state.step3.collections[collectionType]) {
      return;
    }

    const quantityInput = productCard.querySelector('[js-product-card="quantity"]');

    if (!quantityInput || !quantityInput.dataset.productId) {
      return;
    }

    const productId = String(quantityInput.dataset.productId);
    const requestedQuantity = Number(event.detail?.quantity) || 0;

    const collectionMap = this.state.step3.collections[collectionType];
    const currentQuantity = collectionMap.get(productId) || 0;
    const currentTotal = this.getCollectionTotal(collectionType);
    const limit = this.state.step3.limits[collectionType] || 0;
    const maxForCard = Math.max(0, limit - (currentTotal - currentQuantity));
    const nextQuantity = Math.min(Math.max(0, requestedQuantity), maxForCard);

    if (nextQuantity <= 0) {
      collectionMap.delete(productId);
    } else {
      collectionMap.set(productId, nextQuantity);
    }

    setCardQuantity(productCard, nextQuantity);

    if (collectionType === COLLECTION_TYPES.DRINKS && nextQuantity > 0) {
      this.state.step3.drinksDecision = 'add';
      this.state.step3.limits[COLLECTION_TYPES.DRINKS] =
        Math.max(this.state.step3.limits[COLLECTION_TYPES.DRINKS] || 0, nextQuantity);
      this.syncShotPackInputsFromState();
    }

    this.syncCollectionUI(collectionType);
    this.refreshAfterStep3Change();
  }

  updateStep1(nextValues, options = {}) {
    const previousDiet = this.state.step1.diet;
    const previousMealsPerWeek = this.state.step1.mealsPerWeek;
    const previousPortion = this.state.step1.portion;
    const previousStep1Complete = isStep1Complete(this.state.step1);
    let mealsCountChangedInBuild = false;

    if (typeof nextValues.diet === 'string' && FIXED_OPTIONS.DIETS.includes(nextValues.diet)) {
      this.state.step1.diet = nextValues.diet;
    }

    if (typeof nextValues.portion === 'string' && FIXED_OPTIONS.PORTIONS.includes(nextValues.portion)) {
      this.state.step1.portion = nextValues.portion;
    }

    if (Number.isFinite(nextValues.mealsPerWeek) && this.isValidMealsPerWeekOption(nextValues.mealsPerWeek)) {
      this.state.step1.mealsPerWeek = nextValues.mealsPerWeek;
      this.state.step3.limits[COLLECTION_TYPES.MEALS] = nextValues.mealsPerWeek;
    }

    this.enforcePortionDietRules();

    const breakfastRulesChanged = this.enforceBreakfastDietRules(previousDiet);

    this.syncInputsFromState();
    this.setGlobalError('');
    this.updateStep1MealOptionPrices();

    saveStep1(this.state.step1);

    this.updateActionStates();
    this.updateStep1PricingUI();

    const currentStep1Complete = isStep1Complete(this.state.step1);
    if (currentStep1Complete) {
      const shouldForcePreload = previousDiet !== this.state.step1.diet && previousStep1Complete;
      this.preloadStep3CollectionsFromStep1({ force: shouldForcePreload });
    }

    if (this.state.step === STEP_IDS.BUILD) {
      if (previousDiet !== this.state.step1.diet) {
        this.handleDietChangedInBuild();
      } else if (breakfastRulesChanged) {
        this.syncCollectionUI(COLLECTION_TYPES.BREAKFAST);
      }

      if (previousMealsPerWeek !== this.state.step1.mealsPerWeek) {
        mealsCountChangedInBuild = true;
        this.clampCollectionToLimit(COLLECTION_TYPES.MEALS);
        this.syncCollectionUI(COLLECTION_TYPES.MEALS);
      }

      if (previousPortion !== this.state.step1.portion) {
        // If portion size changes, update the meals cards so calories/macros reflect the new
        // portion, and re-run the grid so vegan meals follow the standard-only rule.
        this.syncCollectionUI(COLLECTION_TYPES.MEALS);
        this.syncCollectionNutrition(COLLECTION_TYPES.MEALS);
      }

      if (!options.fromBuildControls) {
        this.syncBuildControlsFromState();
      }

      if (
        mealsCountChangedInBuild &&
        [STAGE_IDS.BREAKFAST, STAGE_IDS.DRINKS].includes(this.state.stage)
      ) {
        this.setStage(STAGE_IDS.MEALS);
        return;
      }

      if (!this.isMealsComplete()) {
        this.setStage(STAGE_IDS.MEALS);
      }
    }

    this.refreshAfterStep3Change();
  }

  updateBreakfastLimitFromControl(value) {
    if (this.isBreakfastDisabledForDiet()) {
      this.enforceBreakfastDietRules();
      this.refreshAfterStep3Change();
      return;
    }

    if (!value) {
      this.state.step3.breakfastSkipped = false;
      this.state.step3.breakfastLimit = 0;
      this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] = 0;
      this.state.step3.collections[COLLECTION_TYPES.BREAKFAST].clear();
      this.syncBreakfastInputsFromState();
      this.syncCollectionUI(COLLECTION_TYPES.BREAKFAST);
      this.refreshAfterStep3Change();
      return;
    }

    if (value === 'skip') {
      this.skipBreakfast();
      return;
    }

    const parsed = Number(value);

    if (this.isValidBreakfastPerWeekOption(parsed)) {
      this.setBreakfastLimit(parsed);
    }
  }

  setBreakfastLimit(limit) {
    if (this.isBreakfastDisabledForDiet()) {
      return;
    }

    if (!this.isValidBreakfastPerWeekOption(limit)) {
      return;
    }

    this.state.step3.breakfastSkipped = false;
    this.state.step3.breakfastLimit = limit;
    this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] = limit;

    this.clampCollectionToLimit(COLLECTION_TYPES.BREAKFAST);
    this.syncBreakfastInputsFromState();
    this.syncCollectionUI(COLLECTION_TYPES.BREAKFAST);

    if (!this.isBreakfastComplete() && this.state.stage === STAGE_IDS.DRINKS) {
      this.setStage(STAGE_IDS.BREAKFAST);
    }

    this.refreshAfterStep3Change();
  }

  skipBreakfast(options = {}) {
    this.state.step3.breakfastSkipped = true;
    this.state.step3.breakfastLimit = 0;
    this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] = 0;
    this.state.step3.collections[COLLECTION_TYPES.BREAKFAST].clear();

    this.syncBreakfastInputsFromState();
    this.syncCollectionUI(COLLECTION_TYPES.BREAKFAST);
    this.refreshAfterStep3Change();

    if (options.autoAdvance && this.state.stage === STAGE_IDS.BREAKFAST) {
      this.handleBreakfastNext();
    }
  }

  setDrinksDecision(decision) {
    if (!['add', 'skip'].includes(decision)) {
      return;
    }

    if (decision === 'add') {
      this.setShotPackQuantity(this.state.step3.limits[COLLECTION_TYPES.DRINKS] || 1);
      return;
    }

    this.state.step3.drinksDecision = 'skip';
    this.state.step3.limits[COLLECTION_TYPES.DRINKS] = 0;
    this.state.step3.collections[COLLECTION_TYPES.DRINKS].clear();

    this.syncShotPackInputsFromState();
    this.syncCollectionUI(COLLECTION_TYPES.DRINKS);
    this.refreshAfterStep3Change();
  }

  // Shot packs are multiples of a single shot product, so choosing a pack is
  // just choosing a quantity (1, 2 or 3) of that product.
  setShotPackQuantity(packQuantity) {
    const maxPackQuantity = Math.max(1, this.elements.shotPackInputs.length);
    const quantity = Math.min(Math.max(Math.round(Number(packQuantity) || 0), 1), maxPackQuantity);
    const drinksCollection = this.state.step3.collections[COLLECTION_TYPES.DRINKS];
    const shotProductId = this.getShotProductId();

    this.state.step3.drinksDecision = 'add';
    this.state.step3.limits[COLLECTION_TYPES.DRINKS] = quantity;

    drinksCollection.clear();

    if (shotProductId) {
      drinksCollection.set(shotProductId, quantity);
    }

    this.syncShotPackInputsFromState();
    this.syncCollectionUI(COLLECTION_TYPES.DRINKS);
    this.refreshAfterStep3Change();
  }

  // Only the first card of the drinks collection is ever shown, so that product is the shot.
  getShotProductId() {
    const drinksRoot = this.elements.collectionRoots[COLLECTION_TYPES.DRINKS];
    const firstDrinkCard = drinksRoot ? getProductCards(drinksRoot)[0] : null;
    const quantityInput = firstDrinkCard?.querySelector('[js-product-card="quantity"]');

    return quantityInput?.dataset?.productId ? String(quantityInput.dataset.productId) : '';
  }

  syncShotPackInputsFromState() {
    const isAdded = this.state.step3.drinksDecision === 'add';
    const packQuantity = this.state.step3.limits[COLLECTION_TYPES.DRINKS] || 0;

    this.elements.shotPackInputs.forEach((input) => {
      input.checked = isAdded && Number(input.value) === packQuantity;
    });

    this.updateDrinksSubtitle();
  }

  // The selected pack size (6, 12 or 18 shots), split across two flavours for the subtitle.
  getSelectedShotTotal() {
    const shotsPerPack = Math.max(1, Math.round(Number(this.dataset.shotsPerPack) || 0));
    const packQuantity = Math.max(1, this.state.step3.limits[COLLECTION_TYPES.DRINKS] || 1);

    return packQuantity * shotsPerPack;
  }

  updateDrinksSubtitle() {
    const subtitleElement = this.elements.drinksSubtitle;
    const template = subtitleElement?.dataset?.template;

    if (!subtitleElement || !template) {
      return;
    }

    const shotTotal = this.getSelectedShotTotal();
    const shotsPerFlavour = Math.max(1, Math.round(shotTotal / 2));

    subtitleElement.textContent = template
      .split('__TOTAL__')
      .join(String(shotTotal))
      .split('__COUNT__')
      .join(String(shotsPerFlavour));
  }

  // The smallest pack is pre-selected, so shots are opted in unless the customer skips.
  // Runs once the drinks collection is on the page, because the selection is keyed by product ID.
  applyDefaultShotPackSelection() {
    if (!this.state.showDrinks || this.state.step3.drinksDecision === 'skip') {
      return;
    }

    if (this.getCollectionTotal(COLLECTION_TYPES.DRINKS) > 0) {
      this.state.step3.drinksDecision = 'add';
      this.syncShotPackInputsFromState();
      return;
    }

    // The default is only applied once the customer actually reaches the shots
    if (this.state.step3.drinksDecision !== 'add' && this.state.stage !== STAGE_IDS.DRINKS) {
      return;
    }

    if (!this.getShotProductId()) {
      return;
    }

    this.setShotPackQuantity(this.state.step3.limits[COLLECTION_TYPES.DRINKS] || 1);
  }

  async handleSelectPlan() {
    if (!isStep1Complete(this.state.step1)) {
      return;
    }

    if (this.shouldSkipContactStep()) {
      await this.goToStep(STEP_IDS.BUILD);
      return;
    }

    await this.goToStep(STEP_IDS.CONTACT);
  }

  shouldSkipContactStep() {
    return this.state.customerLoggedIn || this.state.step2.completed;
  }

  normalizeStepId(value) {
    const parsedStep = Number(value);

    if (!Number.isFinite(parsedStep)) {
      return 0;
    }

    return Math.round(parsedStep);
  }

  canNavigateToStepFromHeader(step) {
    const targetStep = this.normalizeStepId(step);
    const currentStep = this.normalizeStepId(this.state.step);

    if (targetStep < STEP_IDS.PLAN || targetStep > STEP_IDS.BUILD) {
      return false;
    }

    if (targetStep > currentStep) {
      return false;
    }

    if (targetStep === STEP_IDS.CONTACT && this.shouldSkipContactStep()) {
      return false;
    }

    if ((targetStep === STEP_IDS.CONTACT || targetStep === STEP_IDS.BUILD) && !isStep1Complete(this.state.step1)) {
      return false;
    }

    return true;
  }

  async navigateToStepFromHeader(step) {
    if (!this.canNavigateToStepFromHeader(step)) {
      return false;
    }

    const targetStep = this.normalizeStepId(step);
    await this.goToStep(targetStep);
    return true;
  }

  async handleContactSubmit() {
    if (this.isSubmittingContact) {
      return;
    }

    if (this.pendingMarketRedirect) {
      window.location.assign(this.pendingMarketRedirect);
      return;
    }

    const emailInput = this.elements.contactForm?.querySelector('input[name="email"]');
    const postcodeInput = this.elements.contactForm?.querySelector('input[name="postcode"]');
    const email = emailInput?.value.trim() || '';
    const postcode = postcodeInput?.value.trim() || '';

    if (!isValidEmail(email)) {
      this.setContactError('Please enter a valid email address.');
      return;
    }

    if (!postcode) {
      this.setContactError('Please enter your postcode.');
      return;
    }

    this.isSubmittingContact = true;

    if (this.elements.contactSubmit) {
      this.elements.contactSubmit.disabled = true;
    }

    this.setContactError('');

    try {
      const storeMarket = this.getStoreMarketCode();
      
      const signup = await submitSignup({
        email,
        postcode,
        market: storeMarket,
        listId: this.dataset.klaviyoListId,
        source: 'meal plan builder v2'
      });

      if (signup.error) {
        this.setContactError(signup.error);
        return;
      }

      if (signup.customer.isExistingSubscriber) {
        this.setContactError('An account with this email already exists. Please log in instead.');
        return;
      }

      // If the postcode resolves to a different market than this storefront
      // serves, offer to redirect to the correct market's site.
      const addressMarket = this.normalizeMarketCode(signup.delivery?.market);
      
      if (this.shouldRedirectToMarketSite(storeMarket, addressMarket) && !email.includes("@simmereats.com")) {
        this.showMarketMismatch(addressMarket, storeMarket);
        return;
      }

      if (!signup.delivery?.ok) {
        reportMessage('validatePostcode', 'Invalid postcode input', this.dataset.shopId, { postcode });
        this.setContactError(signup.delivery?.error || 'Unable to validate postcode.');
        return;
      }

      if (!signup.delivery.available) {
        this.setContactError("Sorry, we don't currently deliver to this postcode.");
        return;
      }

      const subscribeResult = signup.klaviyo?.subscribe;

      if (subscribeResult && !subscribeResult.ok) {
        this.setContactError(subscribeResult.error || 'Unable to subscribe right now.');
        return;
      }

      saveEmail(email);
      savePostcode(postcode);

      this.state.step2.email = email;
      this.state.step2.postcode = postcode;
      this.state.step2.completed = true;

      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish('generate_lead');
      }

      await this.goToStep(STEP_IDS.BUILD);
    } catch (error) {
      reportError('handleContactSubmit', error, this.dataset.shopId, { email, postcode });
    } finally {
      this.isSubmittingContact = false;

      if (this.elements.contactSubmit) {
        this.elements.contactSubmit.disabled = false;
      }
    }
  }

  handleMealsNext() {
    if (!this.isMealsComplete()) {
      return;
    }

    if (this.isBreakfastDisabledForDiet()) {
      this.goToDrinksStageOrCheckout();
      return;
    }

    this.setStage(STAGE_IDS.BREAKFAST);
  }

  handleBreakfastNext() {
    if (this.isBreakfastDisabledForDiet()) {
      this.goToDrinksStageOrCheckout();
      return;
    }

    if (!this.isBreakfastComplete()) {
      return;
    }

    this.goToDrinksStageOrCheckout();
  }

  goToDrinksStageOrCheckout() {
    if (!this.state.showDrinks) {
      this.handleCheckout();
      return;
    }

    this.setStage(STAGE_IDS.DRINKS);
  }

  handleFooterAction() {
    if (this.state.stage === STAGE_IDS.MEALS) {
      this.handleMealsNext();
      return;
    }

    if (this.state.stage === STAGE_IDS.BREAKFAST) {
      this.handleBreakfastNext();
      return;
    }

    this.handleCheckout();
  }

  handleStageIndicatorClick(stage) {
    if (!stage || !Object.values(STAGE_IDS).includes(stage)) {
      return;
    }

    this.setStage(stage);
  }

  getStageCompletionState() {
    return {
      mealsComplete: this.isMealsComplete(),
      breakfastComplete: this.isBreakfastComplete()
    };
  }

  isMealsComplete() {
    return isMealsStageComplete(this.state.step3, this.stepperCollectionTotal);
  }

  isBreakfastComplete() {
    if (this.isBreakfastDisabledForDiet()) {
      return true;
    }

    return isBreakfastStageComplete(this.state.step3, this.stepperCollectionTotal);
  }

  isDrinksComplete() {
    return !this.state.showDrinks || isDrinksStageComplete(this.state.step3, this.stepperCollectionTotal);
  }

  isVeganDiet(diet = this.state.step1.diet) {
    return String(diet || '').toLowerCase() === 'vegan';
  }

  isBreakfastDisabledForDiet(diet = this.state.step1.diet) {
    return this.isVeganDiet(diet);
  }

  getHiddenPortionsForDiet(diet = this.state.step1.diet) {
    return this.isVeganDiet(diet) ? VEGAN_HIDDEN_PORTIONS : [];
  }

  isSinglePortionDiet(diet = this.state.step1.diet) {
    return this.getHiddenPortionsForDiet(diet).length === FIXED_OPTIONS.PORTIONS.length - 1;
  }

  // Keeps the stored portion valid for the current diet. Runs on hydration and on
  // every step 1 update, so a saved "large" vegan plan is corrected on load too.
  enforcePortionDietRules() {
    if (!this.getHiddenPortionsForDiet().includes(this.state.step1.portion)) {
      return false;
    }

    this.state.step1.portion = DEFAULT_PORTION;

    return true;
  }

  enforceBreakfastDietRules(previousDiet = '') {
    const breakfastDisabled = this.isBreakfastDisabledForDiet();
    const wasBreakfastDisabled = this.isBreakfastDisabledForDiet(previousDiet);
    let changed = false;

    if (breakfastDisabled) {
      if (
        !this.state.step3.breakfastSkipped ||
        this.state.step3.breakfastLimit !== 0 ||
        this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] !== 0 ||
        this.state.step3.collections[COLLECTION_TYPES.BREAKFAST].size > 0
      ) {
        changed = true;
      }

      this.state.step3.breakfastSkipped = true;
      this.state.step3.breakfastLimit = 0;
      this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] = 0;
      this.state.step3.collections[COLLECTION_TYPES.BREAKFAST].clear();

      return changed;
    }

    if (wasBreakfastDisabled && this.state.step3.breakfastSkipped) {
      this.state.step3.breakfastSkipped = false;
      changed = true;
    }

    // Breakfast is enabled and not skipped, so it must carry a count. Zero gets here
    // when the diet was switched to vegan and back — vegan zeroes the limit, and
    // un-skipping alone would leave nothing preselected and the stage unable to complete.
    if (!this.state.step3.breakfastSkipped && this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] <= 0) {
      const defaultBreakfastLimit = this.getDefaultBreakfastPerWeekOption();

      if (defaultBreakfastLimit > 0) {
        this.state.step3.breakfastLimit = defaultBreakfastLimit;
        this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] = defaultBreakfastLimit;
        changed = true;
      }
    }

    return changed;
  }

  getDefaultBreakfastPerWeekOption() {
    const options = this.getBreakfastPerWeekOptions();

    return options.includes(DEFAULT_BREAKFAST_LIMIT) ? DEFAULT_BREAKFAST_LIMIT : (options[0] || 0);
  }

  canEnterStage(stage, stageCompletion = this.getStageCompletionState()) {
    if (!stage || !Object.values(STAGE_IDS).includes(stage)) {
      return false;
    }

    if (stage === STAGE_IDS.MEALS) {
      return true;
    }

    if (stage === STAGE_IDS.BREAKFAST) {
      return stageCompletion.mealsComplete && !this.isBreakfastDisabledForDiet();
    }

    if (stage === STAGE_IDS.DRINKS) {
      if (!this.state.showDrinks) {
        return false;
      }

      if (this.isBreakfastDisabledForDiet()) {
        return stageCompletion.mealsComplete;
      }

      return stageCompletion.breakfastComplete;
    }

    return false;
  }

  getNearestAccessibleStage(stage, stageCompletion = this.getStageCompletionState()) {
    if (this.canEnterStage(stage, stageCompletion)) {
      return stage;
    }

    if (stage === STAGE_IDS.DRINKS && this.canEnterStage(STAGE_IDS.BREAKFAST, stageCompletion)) {
      return STAGE_IDS.BREAKFAST;
    }

    return STAGE_IDS.MEALS;
  }

  toggleDesktopControl(control) {
    if (!this.isValidFilterControl(control)) {
      return;
    }

    if (!this.canInteractWithFilterControl(control)) {
      return;
    }

    const nextControl = this.desktopOpenControl === control ? null : control;

    this.setDesktopOpenControl(nextControl);
  }

  setDesktopOpenControl(control) {
    const nextControl = this.isValidFilterControl(control) ? control : null;

    if (this.desktopOpenControl === nextControl) {
      return;
    }

    this.desktopOpenControl = nextControl;

    this.elements.desktopFilterControls.forEach((element) => {
      const isOpen = element.dataset.control === this.desktopOpenControl;

      element.classList.toggle('is-open', isOpen);
    });
  }

  handleDesktopControlOption(control, value) {
    this.applyControlValue(control, value);
    this.setDesktopOpenControl(null);
  }

  openMobileFilters() {
    if (this.state.step !== STEP_IDS.BUILD) {
      return;
    }

    this.setDesktopOpenControl(null);
    this.mobileFilterDraft = this.createFilterDraftFromState();
    this.mobileFilterAccordionState = this.createEmptyMobileFilterAccordionState();

    this.classList.add('is-mobile-filters-open');

    if (this.elements.mobileFilterOverlay) {
      this.elements.mobileFilterOverlay.hidden = false;
    }

    this.syncFilterControlsUI();
  }

  closeMobileFilters() {
    this.mobileFilterDraft = null;
    this.mobileFilterAccordionState = this.createEmptyMobileFilterAccordionState();

    this.classList.remove('is-mobile-filters-open');

    if (this.elements.mobileFilterOverlay) {
      this.elements.mobileFilterOverlay.hidden = true;
    }

    this.syncFilterControlsUI();
  }

  toggleMobileAccordion(control) {
    if (!this.isValidFilterControl(control)) {
      return;
    }

    if (!this.canInteractWithFilterControl(control)) {
      return;
    }

    this.mobileFilterAccordionState[control] = !this.mobileFilterAccordionState[control];
    this.syncFilterControlsUI();
  }

  handleMobileControlOption(control, value) {
    if (!this.mobileFilterDraft || !this.isValidFilterControl(control)) {
      return;
    }

    this.mobileFilterDraft[control] = value;

    // Picking vegan in the drawer must drop a large/lean draft portion straight away
    if (this.getHiddenPortionsForDiet(this.mobileFilterDraft.diet).includes(this.mobileFilterDraft.portion)) {
      this.mobileFilterDraft.portion = DEFAULT_PORTION;
    }

    this.syncFilterControlsUI();
  }

  applyMobileFilterDraft() {
    if (!this.mobileFilterDraft) {
      this.closeMobileFilters();
      return;
    }

    this.applyControlValue(FILTER_CONTROL_IDS.DIET, this.mobileFilterDraft.diet);
    this.applyControlValue(FILTER_CONTROL_IDS.PORTION, this.mobileFilterDraft.portion);
    this.applyControlValue(FILTER_CONTROL_IDS.MEALS, this.mobileFilterDraft.meals);
    this.applyControlValue(FILTER_CONTROL_IDS.BREAKFAST, this.mobileFilterDraft.breakfast);
    this.applyControlValue(FILTER_CONTROL_IDS.DELIVERY_DAY, this.mobileFilterDraft.deliveryDay);
    this.closeMobileFilters();
  }

  applyControlValue(control, rawValue) {
    const value = String(rawValue || '');

    if (!this.isValidFilterControl(control) || !value) {
      return;
    }

    switch (control) {
      case FILTER_CONTROL_IDS.DIET:
        this.updateStep1({ diet: value }, { fromBuildControls: true });
        return;
      case FILTER_CONTROL_IDS.PORTION:
        this.updateStep1({ portion: value }, { fromBuildControls: true });
        return;
      case FILTER_CONTROL_IDS.MEALS:
        this.updateStep1({ mealsPerWeek: Number(value) }, { fromBuildControls: true });
        return;
      case FILTER_CONTROL_IDS.BREAKFAST:
        this.updateBreakfastLimitFromControl(value);
        return;
      case FILTER_CONTROL_IDS.DELIVERY_DAY:
        this.setSelectedDeliveryDayKey(value);
        return;
      default:
        return;
    }
  }

  createFilterDraftFromState() {
    const defaultMealsValue = this.getDefaultMealsPerWeekOption();
    const selectedDeliveryDayKey = this.getSelectedDeliveryDayKey() || this.deliveryDayOptions[0]?.key || '';

    return {
      diet: this.state.step1.diet || FIXED_OPTIONS.DIETS[0],
      portion: this.state.step1.portion || FIXED_OPTIONS.PORTIONS[0],
      meals: String(this.state.step1.mealsPerWeek || defaultMealsValue),
      breakfast: this.state.step3.breakfastSkipped
        ? 'skip'
        : String(this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] || ''),
      deliveryDay: selectedDeliveryDayKey
    };
  }

  async handleCheckout() {
    if (this.isSubmittingCheckout) {
      return;
    }

    this.setGlobalError('');

    if (!isStep1Complete(this.state.step1)) {
      return;
    }

    if (!this.isMealsComplete()) {
      this.setStage(STAGE_IDS.MEALS);
      return;
    }

    if (!this.isBreakfastComplete()) {
      this.setStage(STAGE_IDS.BREAKFAST);
      return;
    }

    const drinksComplete = this.isDrinksComplete();

    if (!drinksComplete) {
      this.setStage(STAGE_IDS.DRINKS);
      return;
    }

    this.isSubmittingCheckout = true;
    this.updateActionStates();

    try {
      const payloadResult = buildRechargeCartPayload({
        bundleMeta: this.bundleMeta,
        step1: this.state.step1,
        step3: this.state.step3,
        collectionRoots: this.elements.collectionRoots
      });

      if (!payloadResult.ok) {
        this.setGlobalError(payloadResult.error);
        return;
      }

      const rechargeClient = await this.initializeRecharge();

      if (!rechargeClient?.bundle?.getDynamicBundleItems) {
        this.setGlobalError('Recharge bundle client is not available.');
        return;
      }

      const cartPayload = payloadResult.payload;
      const dynamicBundleValidationResult = rechargeClient.bundle.validateDynamicBundle?.(cartPayload.bundle);

      if (dynamicBundleValidationResult !== true) {
        this.setGlobalError(
          typeof dynamicBundleValidationResult === 'string'
            ? dynamicBundleValidationResult
            : 'Recharge rejected the current bundle configuration.'
        );

        return;
      }

      const cartItems = await rechargeClient.bundle.getDynamicBundleItems(
        cartPayload.bundle,
        cartPayload.bundleProductData.handle
      );

      if (!Array.isArray(cartItems) || cartItems.length === 0) {
        this.setGlobalError('No cart items were returned for this selection.');
        return;
      }

      const taggedCartItems = this.tagMealPlanCartItems(cartItems);

      await this.removeExistingMealPlanItems(Number(cartPayload.bundleProductData.productId));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: taggedCartItems,
          attributes: cartPayload.attributes
        })
      });

      if (!response.ok) {
        throw new Error(`Cart add failed with status ${response.status}`);
      }

      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish('add_to_cart');
      }

      window.location.assign(this.buildCheckoutUrl());
    } catch (error) {
      this.setGlobalError(error.message || 'Unable to checkout with your current selection.');
    } finally {
      this.isSubmittingCheckout = false;
      this.updateActionStates();
    }
  }

  tagMealPlanCartItems(cartItems) {
    if (!Array.isArray(cartItems)) {
      return [];
    }

    return cartItems.map((item) => {
      const existingProperties = this.getLineItemProperties(item);

      return {
        ...item,
        properties: {
          ...existingProperties,
          _meal_plan_builder: 'true'
        }
      };
    });
  }

  getLineItemProperties(item) {
    return item?.properties && typeof item.properties === 'object'
      ? item.properties
      : {};
  }

  isMealPlanCartItem(item, bundleProductId) {
    const properties = this.getLineItemProperties(item);
    const hasBuilderMarker = String(properties._meal_plan_builder || '').toLowerCase() === 'true';
    const hasBundleComponents = Boolean(properties._components);

    const parsedBundleProductId = Number(bundleProductId);
    const parsedItemProductId = Number(item?.product_id || 0);
    const isBundleProduct =
      Number.isFinite(parsedBundleProductId) &&
      parsedBundleProductId > 0 &&
      parsedItemProductId === parsedBundleProductId;

    return hasBuilderMarker || hasBundleComponents || isBundleProduct;
  }

  async removeExistingMealPlanItems(bundleProductId) {
    const cartResponse = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!cartResponse.ok) {
      throw new Error(`Cart fetch failed with status ${cartResponse.status}`);
    }

    const cart = await cartResponse.json();
    const cartItems = Array.isArray(cart?.items) ? cart.items : [];
    const lineKeysToRemove = cartItems
      .filter((item) => this.isMealPlanCartItem(item, bundleProductId))
      .map((item) => String(item?.key || ''))
      .filter(Boolean);

    if (lineKeysToRemove.length === 0) {
      return;
    }

    const updates = {};

    lineKeysToRemove.forEach((lineKey) => {
      updates[lineKey] = 0;
    });

    const removeResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates
      })
    });

    if (!removeResponse.ok) {
      throw new Error(`Cart replace failed with status ${removeResponse.status}`);
    }
  }

  async goToStep(step) {
    const previousStep = this.state.step;

    this.state.step = step;
    this.applyStepUI();

    if (previousStep !== step) {
      this.scrollToTop();
    }

    if (step === STEP_IDS.BUILD) {
      if (!this.state.stage) {
        this.state.stage = STAGE_IDS.MEALS;
      }

      if (this.step3PreloadPromise) {
        await this.step3PreloadPromise;
      } else {
        await this.ensureCollectionsLoaded();
      }
      this.applyStageUI();
    }

    this.updateActionStates();
    this.updateFooter();
  }

  preloadStep3CollectionsFromStep1(options = {}) {
    if (this.state.step === STEP_IDS.BUILD) {
      return;
    }

    if (!isStep1Complete(this.state.step1)) {
      return;
    }

    const { force = false } = options;
    if (this.step3PreloadPromise && !force) {
      return;
    }

    const preloadPromise = this.ensureCollectionsLoaded(force);
    this.step3PreloadPromise = preloadPromise;

    preloadPromise.finally(() => {
      if (this.step3PreloadPromise === preloadPromise) {
        this.step3PreloadPromise = null;
      }
    });
  }

  setStage(stage) {
    if (!this.canEnterStage(stage)) {
      return;
    }

    const previousStage = this.state.stage;

    this.state.stage = stage;

    if (stage === STAGE_IDS.DRINKS) {
      this.applyDefaultShotPackSelection();
    }

    this.applyStageUI();
    this.updateActionStates();
    this.refreshAfterStep3Change();

    if (previousStage !== stage) {
      this.scrollToTop();
    }
  }

  getBreakfastCollectionHandles(diet = this.state.step1.diet) {
    if (this.isBreakfastDisabledForDiet(diet)) {
      return [];
    }

    const meatBreakfastHandle = this.config?.diets?.meat?.breakfast || '';
    const veganBreakfastHandle = this.config?.diets?.vegan?.breakfast || '';

    return [...new Set([meatBreakfastHandle, veganBreakfastHandle].filter(Boolean))];
  }

  async ensureCollectionsLoaded(force = false) {
    const selectedDiet = this.state.step1.diet;
    const dietConfig = this.config?.diets?.[selectedDiet] || {};
    const requestId = ++this.loadingRequestId;

    const handlesByType = {
      [COLLECTION_TYPES.MEALS]: [dietConfig.meals || ''],
      [COLLECTION_TYPES.BREAKFAST]: this.getBreakfastCollectionHandles(selectedDiet),
      [COLLECTION_TYPES.DRINKS]: [this.config?.drinksHandle || 'drinks']
    };

    const loadTasks = Object.entries(handlesByType).map(async ([collectionType, handles]) => {
      const contentElement = this.elements.collectionContents[collectionType];

      if (!contentElement) {
        return;
      }

      const normalizedHandles = (handles || []).filter(Boolean);
      const cacheKey = normalizedHandles.join('+');

      if (!cacheKey) {
        contentElement.innerHTML = '';
        this.renderedHandles[collectionType] = '';
        return;
      }

      if (!force && this.renderedHandles[collectionType] === cacheKey) {
        this.syncCollectionUI(collectionType);
        this.syncCollectionNutrition(collectionType);
        return;
      }

      try {
        const fetchOptions = collectionType === COLLECTION_TYPES.MEALS
          ? {
            minProductCards: Math.max(
              1,
              Number(this.state.step3.limits[COLLECTION_TYPES.MEALS]) || Number(this.state.step1.mealsPerWeek) || 0
            ),
            maxAttempts: 4
          }
          : {};
        const htmlParts = await Promise.all(
          normalizedHandles.map((handle) => fetchCollectionHtml(handle, this.collectionCache, fetchOptions))
        );

        if (requestId !== this.loadingRequestId) {
          return;
        }

        const mergedHtml = normalizedHandles.length > 1
          ? mergeCollectionHtmlByProduct(htmlParts)
          : (htmlParts[0] || '');

        const html = collectionType === COLLECTION_TYPES.MEALS
          ? moveTaggedCardsToEnd(mergedHtml, VEGAN_TAG)
          : mergedHtml;

        contentElement.innerHTML = html;

        this.renderedHandles[collectionType] = cacheKey;
        this.pruneCollectionToLoadedProducts(collectionType);
        this.syncCollectionUI(collectionType);
        this.syncCollectionNutrition(collectionType);
      } catch (error) {
        this.setGlobalError(error.message || `Unable to load ${collectionType}.`);
      }
    });

    await Promise.all(loadTasks);

    if (requestId === this.loadingRequestId && this.pendingSessionRevalidation) {
      this.pendingSessionRevalidation = false;
      this.revalidateSessionSelectionsAfterLoad();
    }

    if (requestId === this.loadingRequestId) {
      this.applyDefaultShotPackSelection();
    }

    this.updateFooter();
  }

  revalidateSessionSelectionsAfterLoad() {
    // Pruning during load may have removed selections whose products are no
    // longer on this week's menu. Re-clamp to the configured limits and snap
    // back to the earliest stage that is no longer satisfied so the meals grid
    // is shown and the customer can rebuild from the current menu.
    this.clampCollectionToLimit(COLLECTION_TYPES.MEALS);
    this.clampCollectionToLimit(COLLECTION_TYPES.BREAKFAST);
    this.clampCollectionToLimit(COLLECTION_TYPES.DRINKS);

    this.state.stage = this.getNearestAccessibleStage(this.state.stage);

    this.syncCollectionUI(COLLECTION_TYPES.MEALS);
    this.syncCollectionUI(COLLECTION_TYPES.BREAKFAST);
    this.syncCollectionUI(COLLECTION_TYPES.DRINKS);
    this.syncCollectionNutrition(COLLECTION_TYPES.MEALS);
    this.syncBreakfastInputsFromState();
    this.syncShotPackInputsFromState();
    this.refreshAfterStep3Change();
  }

  setCollectionLoadingState(collectionTypes, isLoading) {
    const shouldEnable = Boolean(isLoading);
    const targetTypes = Array.isArray(collectionTypes) ? collectionTypes : [];

    targetTypes.forEach((collectionType) => {
      const collectionRoot = this.elements.collectionRoots[collectionType];

      if (!collectionRoot) {
        return;
      }

      collectionRoot.classList.toggle('is-loading', shouldEnable);
    });
  }

  handleDietChangedInBuild() {
    this.state.step3.collections[COLLECTION_TYPES.MEALS].clear();
    this.state.step3.collections[COLLECTION_TYPES.BREAKFAST].clear();
    this.state.step3.collections[COLLECTION_TYPES.DRINKS].clear();
    this.state.stage = STAGE_IDS.MEALS;

    const loadingCollections = [COLLECTION_TYPES.MEALS, COLLECTION_TYPES.BREAKFAST];
    this.setCollectionLoadingState(loadingCollections, true);

    const loadPromise = this.ensureCollectionsLoaded(true);
    const loadRequestId = this.loadingRequestId;

    loadPromise.finally(() => {
      if (loadRequestId === this.loadingRequestId) {
        this.setCollectionLoadingState(loadingCollections, false);
      }
    });
  }

  pruneCollectionToLoadedProducts(collectionType) {
    const collectionRoot = this.elements.collectionRoots[collectionType];

    if (!collectionRoot) {
      return;
    }

    const productIds = new Set();

    getProductCards(collectionRoot).forEach((card) => {
      const input = card.querySelector('[js-product-card="quantity"]');

      if (input?.dataset?.productId) {
        productIds.add(String(input.dataset.productId));
      }
    });

    const collectionMap = this.state.step3.collections[collectionType];

    Array.from(collectionMap.keys()).forEach((productId) => {
      if (!productIds.has(productId)) {
        collectionMap.delete(productId);
      }
    });
  }

  clampCollectionToLimit(collectionType) {
    const limit = this.state.step3.limits[collectionType] || 0;
    const sourceMap = this.state.step3.collections[collectionType];
    const nextEntries = [];

    let remaining = limit;

    sourceMap.forEach((quantity, productId) => {
      if (remaining <= 0) {
        return;
      }

      const nextQuantity = Math.min(quantity, remaining);

      if (nextQuantity > 0) {
        nextEntries.push([productId, nextQuantity]);
        remaining -= nextQuantity;
      }
    });

    sourceMap.clear();

    nextEntries.forEach(([productId, quantity]) => {
      sourceMap.set(productId, quantity);
    });
  }

  syncCollectionUI(collectionType) {
    const collectionRoot = this.elements.collectionRoots[collectionType];

    if (!collectionRoot) {
      return;
    }

    if (collectionType === COLLECTION_TYPES.MEALS) {
      this.syncVeganMealAvailability(collectionRoot);
    }

    const collectionMap = this.state.step3.collections[collectionType];
    const limit = this.state.step3.limits[collectionType] || 0;

    syncCollectionCardQuantities(collectionRoot, collectionMap);
    updateCollectionIncrementButtons(collectionRoot, collectionMap, limit);
  }

  // Vegetarian meals are also tagged vegan, so they keep following the vegan
  // standard-portion-only rule and are hidden from vegan only page
  syncVeganMealAvailability(collectionRoot) {
    const hideVeganMeals = this.state.step1.portion !== DEFAULT_PORTION;
    const hideVegetarianMeals = this.isVeganDiet();
    const collectionMap = this.state.step3.collections[COLLECTION_TYPES.MEALS];

    getProductCards(collectionRoot).forEach((card) => {
      if (!cardHasTag(card, VEGAN_TAG)) {
        return;
      }

      const hideCard = hideVeganMeals || (hideVegetarianMeals && cardHasTag(card, VEGETARIAN_TAG));

      card.hidden = hideCard;

      const productId = getCardProductId(card);

      if (hideCard && productId) {
        collectionMap.delete(productId);
      }
    });
  }

  // Nutrition depends only on the selected portion, so it is refreshed separately
  // from syncCollectionUI on re-render and portion change. Only meals have per-portion
  // variants — breakfast and drinks are single-size, so their nutrition never changes.
  syncCollectionNutrition(collectionType) {
    if (collectionType !== COLLECTION_TYPES.MEALS) {
      return;
    }

    const collectionRoot = this.elements.collectionRoots[collectionType];

    if (!collectionRoot) {
      return;
    }

    const planSize = this.state.step3.limits[collectionType] || 0;

    syncCollectionCardNutrition(collectionRoot, collectionType, this.state.step1.portion, planSize);
  }

  getCollectionTotal(collectionTypeOrMap) {
    if (collectionTypeOrMap && typeof collectionTypeOrMap.forEach === 'function') {
      return getCollectionTotal(collectionTypeOrMap);
    }

    return getCollectionTotal(this.state.step3.collections[collectionTypeOrMap]);
  }

  syncInputsFromState() {
    this.syncStep1InputsFromState();
    this.syncBuildControlsFromState();
    this.syncBreakfastInputsFromState();
    this.syncShotPackInputsFromState();
  }

  syncStep1InputsFromState() {
    this.elements.step1Inputs.diet.forEach((input) => {
      input.checked = input.value === this.state.step1.diet;
    });

    this.elements.step1Inputs.portion.forEach((input) => {
      input.checked = input.value === this.state.step1.portion;
    });

    this.elements.step1Inputs.meals.forEach((input) => {
      input.checked = Number(input.value) === this.state.step1.mealsPerWeek;
    });

    this.syncPortionOptionsUI();
  }

  // Hides the portion sizes the current diet cannot use — the step 1 swatches, their
  // footnotes and the hidden control select.
  syncPortionOptionsUI() {
    const hiddenPortions = this.getHiddenPortionsForDiet();
    const isSinglePortion = this.isSinglePortionDiet();

    this.elements.step1PortionSizeContainer.forEach((container) => {
      container.hidden = hiddenPortions.includes(container.dataset.portion);
    });

    this.elements.step1PortionFootnoteItems.forEach((footnote) => {
      footnote.hidden = hiddenPortions.includes(footnote.dataset.portion);
    });

    this.elements.step1PortionGrid?.classList.toggle('is-single-portion', isSinglePortion);
    this.elements.step1PortionFootnotes?.classList.toggle('is-single-portion', isSinglePortion);

    Array.from(this.elements.controls.portion?.options || []).forEach((optionElement) => {
      optionElement.hidden = hiddenPortions.includes(optionElement.value);
    });
  }

  syncBuildControlsFromState() {
    const defaultMealsValue = this.getDefaultMealsPerWeekOption();

    if (this.elements.controls.diet) {
      this.elements.controls.diet.value = this.state.step1.diet || FIXED_OPTIONS.DIETS[0];
    }

    if (this.elements.controls.portion) {
      this.elements.controls.portion.value = this.state.step1.portion || FIXED_OPTIONS.PORTIONS[0];
    }

    if (this.elements.controls.meals) {
      this.elements.controls.meals.value = String(this.state.step1.mealsPerWeek || defaultMealsValue);
    }

    if (this.elements.controls.breakfast) {
      if (this.state.step3.breakfastSkipped) {
        this.elements.controls.breakfast.value = 'skip';
      } else if (this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] > 0) {
        this.elements.controls.breakfast.value = String(this.state.step3.limits[COLLECTION_TYPES.BREAKFAST]);
      } else {
        this.elements.controls.breakfast.value = '';
      }
    }

    if (this.elements.controls.deliveryDay) {
      this.elements.controls.deliveryDay.value = this.getSelectedDeliveryDayKey();
    }

    this.syncFilterControlsUI();
  }

  syncFilterControlsUI() {
    const activeValues = this.createFilterDraftFromState();
    const mobileValues = this.mobileFilterDraft || activeValues;
    const hasBreakfastSelection = this.hasBreakfastSelection();
    const hasMultipleDeliveryDayOptions = this.hasMultipleDeliveryDayOptions();
    // The drawer edits a draft, so its portion options follow the draft diet rather than
    // the committed one — otherwise they only update after Apply.
    const mobileHiddenPortions = this.getHiddenPortionsForDiet(mobileValues.diet);
    const hasSingleMobilePortion = this.isSinglePortionDiet(mobileValues.diet);

    if (!hasBreakfastSelection && this.desktopOpenControl === FILTER_CONTROL_IDS.BREAKFAST) {
      this.desktopOpenControl = null;
    }

    if (!hasMultipleDeliveryDayOptions && this.desktopOpenControl === FILTER_CONTROL_IDS.DELIVERY_DAY) {
      this.desktopOpenControl = null;
    }

    if (!hasMultipleDeliveryDayOptions) {
      this.mobileFilterAccordionState[FILTER_CONTROL_IDS.DELIVERY_DAY] = false;
    }

    if (hasSingleMobilePortion) {
      this.mobileFilterAccordionState[FILTER_CONTROL_IDS.PORTION] = false;
    }

    this.elements.controlValueLabels.forEach((label) => {
      const control = label.dataset.control;
      const context = label.dataset.context;

      if (!this.isValidFilterControl(control)) {
        return;
      }

      const source = context === 'mobile' ? mobileValues : activeValues;

      label.textContent = this.getFilterLabel(control, source[control], context);
    });

    this.elements.desktopFilterControls.forEach((controlElement) => {
      const control = controlElement.dataset.control;

      if (control === FILTER_CONTROL_IDS.BREAKFAST) {
        controlElement.classList.toggle('is-visible', hasBreakfastSelection);
      }

      if (control === FILTER_CONTROL_IDS.DELIVERY_DAY) {
        controlElement.classList.toggle('is-static', !hasMultipleDeliveryDayOptions);
      }

      const isOpen = controlElement.dataset.control === this.desktopOpenControl;

      controlElement.classList.toggle('is-open', isOpen);
    });

    this.querySelectorAll('[js-meal-plan-builder="desktop-filter-option"]').forEach((optionElement) => {
      const control = optionElement.dataset.control;
      const isSelected = activeValues[control] === optionElement.dataset.value;

      optionElement.classList.toggle('is-selected', isSelected);
    });

    this.elements.mobileFilterAccordions.forEach((accordionElement) => {
      const control = accordionElement.dataset.control;

      if (control === FILTER_CONTROL_IDS.BREAKFAST) {
        accordionElement.classList.toggle('is-visible', hasBreakfastSelection);
      }

      if (control === FILTER_CONTROL_IDS.DELIVERY_DAY) {
        accordionElement.classList.toggle('is-static', !hasMultipleDeliveryDayOptions);
      }

      if (control === FILTER_CONTROL_IDS.PORTION) {
        accordionElement.classList.toggle('is-static', hasSingleMobilePortion);
      }

      const isOpen = Boolean(this.mobileFilterAccordionState[control]);

      accordionElement.classList.toggle('is-open', isOpen);
    });

    this.querySelectorAll('[js-meal-plan-builder="mobile-filter-option"]').forEach((optionElement) => {
      const control = optionElement.dataset.control;
      const isSelected = mobileValues[control] === optionElement.dataset.value;

      if (control === FILTER_CONTROL_IDS.PORTION) {
        optionElement.hidden = mobileHiddenPortions.includes(optionElement.dataset.value);
      }

      optionElement.classList.toggle('is-selected', isSelected);
    });
  }

  getFilterLabel(control, rawValue, context) {
    const value = String(rawValue || '');

    if (control === FILTER_CONTROL_IDS.DELIVERY_DAY) {
      const selectedOption =
        this.getDeliveryDayOptionByKey(value) ||
        this.getDeliveryDayOptionByKey(this.getSelectedDeliveryDayKey()) ||
        this.deliveryDayOptions[0] ||
        null;

      return selectedOption?.label || '';
    }

    if (!value) {
      return '';
    }

    switch (control) {
      case FILTER_CONTROL_IDS.DIET:
        return this.getDietFilterLabel(value, context);
      case FILTER_CONTROL_IDS.PORTION:
        return value.charAt(0).toUpperCase() + value.slice(1);
      case FILTER_CONTROL_IDS.MEALS: {
        const mealsShortLabel = this.dataset.filterMealsShort || 'meals per week';
        return `${value} ${mealsShortLabel}`;
      }
      case FILTER_CONTROL_IDS.BREAKFAST: {
        if (value === 'skip') {
          return this.dataset.filterBreakfastSkip || 'Skip breakfast';
        }

        const breakfastShortLabel = this.dataset.filterBreakfastShort || 'breakfasts per week';
        return `${value} ${breakfastShortLabel}`;
      }
      default:
        return value;
    }
  }

  decodeHtmlEntities(value) {
    const rawValue = String(value || '');

    if (!rawValue || !rawValue.includes('&') || typeof document === 'undefined') {
      return rawValue;
    }

    const decoder = document.createElement('textarea');
    decoder.innerHTML = rawValue;
    return decoder.value;
  }

  getDietFilterLabel(value, context) {
    const normalizedValue = String(value || '').toLowerCase();
    const dietLabels = {
      meat: this.decodeHtmlEntities(this.dataset.filterDietMeat || 'Meat only'),
      vegan: this.decodeHtmlEntities(this.dataset.filterDietVegan || 'Vegan only'),
      mixed: this.decodeHtmlEntities(this.dataset.filterDietMixed || 'Meat & vegan')
    };

    if (context === 'desktop') {
      dietLabels.mixed = this.decodeHtmlEntities(this.dataset.filterDietMixedDesktop || dietLabels.mixed);
    }

    return dietLabels[normalizedValue] || dietLabels.mixed;
  }

  async loadStep1DiscountDetails() {
    const urlDiscountCode = this.getDiscountCodeFromUrlParam();

    if (urlDiscountCode) {
      this.setDiscountCodeCookie(urlDiscountCode);
    }

    const cookieDiscountCode = this.getDiscountCode();
    const defaultMarketDiscountCode = this.getDefaultMarketDiscountCode();
    const candidateCodes = [];

    if (urlDiscountCode) {
      candidateCodes.push({
        code: urlDiscountCode,
        source: 'url'
      });
    }

    if (
      cookieDiscountCode &&
      (!urlDiscountCode || cookieDiscountCode.toLowerCase() !== urlDiscountCode.toLowerCase())
    ) {
      candidateCodes.push({
        code: cookieDiscountCode,
        source: 'cookie'
      });
    }

    if (
      defaultMarketDiscountCode &&
      !candidateCodes.some((candidate) => candidate.code.toLowerCase() === defaultMarketDiscountCode.toLowerCase())
    ) {
      candidateCodes.push({
        code: defaultMarketDiscountCode,
        source: 'default'
      });
    }

    this.step1Discount = this.getDefaultStep1Discount();

    for (const candidate of candidateCodes) {
      const validatedDiscount = await this.validateDiscountCode(candidate.code);

      if (!validatedDiscount.ok) {
        continue;
      }

      this.step1Discount = validatedDiscount;

      if (candidate.source === 'url' || candidate.source === 'default') {
        this.setDiscountCodeCookie(validatedDiscount.code || candidate.code);
      }

      break;
    }

    this.updateStep1MealOptionPrices();
    this.updateBreakfastOptionPrices();
    this.updateShotPackOptionPrices();
    this.updateStep1PricingUI();
  }

  async validateDiscountCode(discountCode) {
    const normalizedCode = String(discountCode || '').trim();

    if (!normalizedCode) {
      return this.getDefaultStep1Discount();
    }

    const discountResult = await fetchDiscountDetails(normalizedCode);
    const parsedValue = Number(discountResult?.value);
    const isValidPercentageDiscount =
      discountResult?.ok &&
      discountResult?.type === 'percentage' &&
      Number.isFinite(parsedValue) &&
      parsedValue > 0;

    if (!isValidPercentageDiscount) {
      return this.getDefaultStep1Discount();
    }

    return {
      ok: true,
      code: String(discountResult.code || normalizedCode).trim(),
      type: 'percentage',
      value: Math.min(Math.max(parsedValue, 0), 1)
    };
  }

  getDefaultStep1Discount() {
    return {
      ok: false,
      code: '',
      type: '',
      value: 0
    };
  }

  getCurrencyCode() {
    const candidate =
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.code ||
      window.Shopify?.checkoutCurrency ||
      '';

    return String(candidate || 'GBP').trim() || 'GBP';
  }

  getMoneyFormatter() {
    const locale = String(window.Shopify?.locale || '').trim() || 'en-GB';
    const market = String(window.Shopify?.country || '').trim();
    const localeWithMarket = market ? `${locale}-${market}` : locale;
    const currencyCode = this.getCurrencyCode();

    if (
      this.moneyFormatter &&
      this.moneyFormatterCurrencyCode === currencyCode &&
      this.moneyFormatterLocale === localeWithMarket
    ) {
      return this.moneyFormatter;
    }

    try {
      this.moneyFormatter = new Intl.NumberFormat(localeWithMarket, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch (error) {
      this.moneyFormatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    this.moneyFormatterLocale = localeWithMarket;
    this.moneyFormatterCurrencyCode = currencyCode;

    return this.moneyFormatter;
  }

  formatMoney(value) {
    const parsedValue = Number(value);

    if (!Number.isFinite(parsedValue)) {
      return this.getMoneyFormatter().format(0);
    }

    const roundedValue = Math.round((parsedValue + Number.EPSILON) * 100) / 100;

    return this.getMoneyFormatter().format(roundedValue);
  }

  formatPortionAddonLabel(addonPerMeal, isComparePrice = false) {
    const formattedAddon = this.formatMoney(addonPerMeal);
    const labelTemplate = this.decodeHtmlEntities(this.dataset.step1PortionAddonLabelTemplate || '');

    if (isComparePrice) {
      return `(${formattedAddon})`;
    }

    if (labelTemplate && labelTemplate.includes('__AMOUNT__')) {
      return labelTemplate.replace('__AMOUNT__', formattedAddon);
    }

    return `+${formattedAddon}/meal`;
  }

  updateStep1PortionAddonLabels() {
    const selectedMealsInput = this.getCheckedInput(this.elements.step1Inputs.meals)
      || this.elements.step1Inputs.meals[0];
    const selectedMealAmount = Number(selectedMealsInput?.value) || 0;
    const standardPricing = this.getMealOptionPricing(selectedMealAmount, 'standard');
    const discountRate = this.getActiveStep1DiscountRate();

    this.elements.step1Inputs.portion.forEach((portionInput) => {
      const optionElement = portionInput.closest('.meal-plan-builder__option--portion');
      const addonElement = optionElement?.querySelector('.meal-plan-builder__swatch-meta--portion');
      const compareElement = optionElement?.querySelector('[js-meal-plan-builder="step1-portion-addon-compare"]');
      const groupElement = optionElement?.querySelector('[js-meal-plan-builder="step1-portion-addon-group"]');

      if (!addonElement) {
        return;
      }

      const hideAddon = () => {
        addonElement.hidden = true;

        if (compareElement) {
          compareElement.hidden = true;
        }

        if (groupElement) {
          groupElement.hidden = true;
        }
      };

      const normalizedPortion = this.normalizePortionSize(portionInput.value);

      if (!normalizedPortion || normalizedPortion === 'standard' || !standardPricing) {
        hideAddon();
        return;
      }

      const portionPricing = this.getMealOptionPricing(selectedMealAmount, normalizedPortion);

      if (!portionPricing) {
        hideAddon();
        return;
      }

      const addonPerMeal = portionPricing.basePerMeal - standardPricing.basePerMeal;

      if (addonPerMeal <= 0) {
        hideAddon();
        return;
      }

      // The Step 1 percentage discount applies to the whole subtotal, so the portion
      // upcharge is discounted at the same rate.
      const discountedAddonPerMeal = discountRate > 0
        ? Math.max(addonPerMeal * (1 - discountRate), 0)
        : addonPerMeal;
      const showCompare = discountedAddonPerMeal < addonPerMeal - 0.0001;

      addonElement.textContent = this.formatPortionAddonLabel(discountedAddonPerMeal);
      addonElement.hidden = false;

      if (compareElement) {
        compareElement.textContent = showCompare ? this.formatPortionAddonLabel(addonPerMeal, true) : '';
        compareElement.hidden = !showCompare;
      }

      if (groupElement) {
        groupElement.hidden = false;
      }
    });
  }

  parsePriceNumber(value) {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    const stringValue = String(value).trim();
    const normalizedValue = stringValue
      .replace(/,/g, '.')
      .replace(/[^0-9.-]/g, '');

    const parsedValue = Number(normalizedValue);

    if (!Number.isFinite(parsedValue)) {
      return 0;
    }

    return parsedValue;
  }

  normalizePortionSize(value) {
    const normalizedValue = String(value || '').trim().toLowerCase();

    if (FIXED_OPTIONS.PORTIONS.includes(normalizedValue)) {
      return normalizedValue;
    }

    if (normalizedValue === 'regular') {
      return 'standard';
    }

    return '';
  }

  getVariantOptionValues(variant) {
    const optionValues = [];

    if (!variant || typeof variant !== 'object') {
      return optionValues;
    }

    ['option1', 'option2', 'option3'].forEach((optionKey) => {
      const optionValue = variant[optionKey];

      if (typeof optionValue === 'string' && optionValue.trim()) {
        optionValues.push(optionValue.trim());
      }
    });

    if (Array.isArray(variant.options)) {
      variant.options.forEach((optionValue) => {
        if (typeof optionValue === 'string' && optionValue.trim()) {
          optionValues.push(optionValue.trim());
        }
      });
    }

    return [...new Set(optionValues)];
  }

  getVariantAmountOption(variant) {
    const optionValues = this.getVariantOptionValues(variant);

    for (const optionValue of optionValues) {
      const match = optionValue.match(/\d+/);

      if (!match) {
        continue;
      }

      const parsedAmount = Number(match[0]);

      if (Number.isInteger(parsedAmount) && parsedAmount > 0) {
        return parsedAmount;
      }
    }

    const variantTitle = typeof variant?.title === 'string' ? variant.title : '';
    const titleMatch = variantTitle.match(/\d+/);

    if (!titleMatch) {
      return 0;
    }

    const parsedAmount = Number(titleMatch[0]);

    return Number.isInteger(parsedAmount) && parsedAmount > 0 ? parsedAmount : 0;
  }

  getVariantPortionOption(variant) {
    const optionValues = this.getVariantOptionValues(variant);

    for (const optionValue of optionValues) {
      const normalizedPortion = this.normalizePortionSize(optionValue);

      if (normalizedPortion) {
        return normalizedPortion;
      }
    }

    const variantTitle = typeof variant?.title === 'string' ? variant.title : '';
    const normalizedTitle = variantTitle.toLowerCase();

    for (const portion of FIXED_OPTIONS.PORTIONS) {
      if (normalizedTitle.includes(portion)) {
        return portion;
      }
    }

    if (normalizedTitle.includes('regular')) {
      return 'standard';
    }

    return '';
  }

  normalizeVariantPrice(value) {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      if (Number.isInteger(value)) {
        return value / 100;
      }

      return value;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return 0;
      }

      if (/^-?\d+$/.test(trimmedValue)) {
        return Number(trimmedValue) / 100;
      }

      return this.parsePriceNumber(trimmedValue);
    }

    return 0;
  }

  buildBreakfastPricingReferenceLookup(product) {
    const lookup = {};

    if (!product || !Array.isArray(product.variants)) {
      return lookup;
    }

    product.variants.forEach((variant) => {
      const amount = this.getVariantAmountOption(variant);

      if (!amount) {
        return;
      }

      const variantPrice = this.normalizeVariantPrice(variant.price);

      if (variantPrice <= 0) {
        return;
      }

      const variantComparePrice = this.normalizeVariantPrice(variant.compare_at_price);
      const normalizedPricing = this.normalizePerMealPricing(
        variantPrice,
        variantComparePrice > 0 ? variantComparePrice : 0
      );

      if (normalizedPricing.basePerMeal <= 0) {
        return;
      }

      lookup[String(amount)] = normalizedPricing;
    });

    return lookup;
  }

  buildPricingReferenceLookup(product) {
    const lookup = {
      standard: {},
      large: {},
      lean: {}
    };

    if (!product || !Array.isArray(product.variants)) {
      return lookup;
    }

    product.variants.forEach((variant) => {
      const portion = this.getVariantPortionOption(variant);
      const amount = this.getVariantAmountOption(variant);

      if (!portion || !amount) {
        return;
      }

      const variantPrice = this.normalizeVariantPrice(variant.price);

      if (variantPrice <= 0) {
        return;
      }

      const variantComparePrice = this.normalizeVariantPrice(variant.compare_at_price);
      const normalizedPricing = this.normalizePerMealPricing(
        variantPrice,
        variantComparePrice > 0 ? variantComparePrice : 0
      );

      if (normalizedPricing.basePerMeal <= 0) {
        return;
      }

      lookup[portion][String(amount)] = normalizedPricing;
    });

    return lookup;
  }

  getMealOptionPricing(amount, portion = this.state.step1.portion) {
    const parsedAmount = Number(amount);

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      return null;
    }

    const normalizedPortion = this.normalizePortionSize(portion) || 'standard';
    const amountKey = String(parsedAmount);
    const portionCandidates = normalizedPortion === 'standard'
      ? ['standard']
      : [normalizedPortion, 'standard'];

    for (const portionKey of portionCandidates) {
      const pricing = this.pricingReferenceLookup?.[portionKey]?.[amountKey];

      if (pricing && pricing.basePerMeal > 0) {
        return pricing;
      }
    }

    return null;
  }

  getBreakfastOptionPricing(amount) {
    const parsedAmount = Number(amount);

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      return null;
    }

    const pricing = this.breakfastPricingReferenceLookup?.[String(parsedAmount)];

    return pricing && pricing.basePerMeal > 0 ? pricing : null;
  }


  getPerShotPricing() {
    const packPrice = this.normalizeVariantPrice(this.dataset.shotPackPrice);
    const shotsPerPack = Math.max(1, Math.round(Number(this.dataset.shotsPerPack) || 0));

    if (packPrice <= 0) {
      return null;
    }

    const discountRate = this.getActiveStep1DiscountRate();
    const basePerShot = packPrice / shotsPerPack;
    const finalPerShot = discountRate > 0 ? Math.max(basePerShot * (1 - discountRate), 0) : basePerShot;

    return {
      basePerShot,
      finalPerShot,
      showCompare: finalPerShot < basePerShot - 0.0001
    };
  }

  normalizePerMealPricing(basePerMealCandidate, comparePerMealCandidate) {
    let basePerMeal = Number(basePerMealCandidate) || 0;
    let comparePerMeal = Number(comparePerMealCandidate) || 0;

    // Guard against reversed values (compare price < base price).
    if (basePerMeal > 0 && comparePerMeal > 0 && comparePerMeal < basePerMeal) {
      const nextBasePerMeal = comparePerMeal;
      comparePerMeal = basePerMeal;
      basePerMeal = nextBasePerMeal;
    }

    if (comparePerMeal <= basePerMeal) {
      comparePerMeal = 0;
    }

    return {
      basePerMeal,
      comparePerMeal
    };
  }

  getActiveStep1DiscountRate() {
    if (
      !this.step1Discount?.ok ||
      this.step1Discount?.type !== 'percentage'
    ) {
      return 0;
    }

    const parsedDiscountValue = Number(this.step1Discount.value);

    if (!Number.isFinite(parsedDiscountValue) || parsedDiscountValue <= 0) {
      return 0;
    }

    return Math.min(Math.max(parsedDiscountValue, 0), 1);
  }

  getSelectedMealsOptionPricing() {
    const selectedMealsInput = this.getCheckedInput(this.elements.step1Inputs.meals);

    if (!selectedMealsInput) {
      return null;
    }

    return this.getMealOptionPricing(Number(selectedMealsInput.value), this.state.step1.portion);
  }

  applyPerMealPricingToElements({
    amount,
    portion,
    baseElement,
    compareElement
  }) {
    if (!baseElement) {
      return;
    }

    const pricing = this.getMealOptionPricing(amount, portion);

    if (!pricing) {
      baseElement.textContent = '';

      if (compareElement) {
        compareElement.hidden = true;
      }

      return;
    }

    const discountRate = this.getActiveStep1DiscountRate();
    const hasDiscount = discountRate > 0;

    if (hasDiscount) {
      const discountedPerMeal = Math.max(pricing.basePerMeal * (1 - discountRate), 0);
      baseElement.textContent = `${this.formatMoney(discountedPerMeal)}/meal`;

      if (compareElement) {
        compareElement.textContent = this.formatMoney(pricing.basePerMeal);
        compareElement.hidden = false;
      }

      return;
    }

    baseElement.textContent = `${this.formatMoney(pricing.basePerMeal)}/meal`;

    if (!compareElement) {
      return;
    }

    if (pricing.comparePerMeal > 0) {
      compareElement.textContent = this.formatMoney(pricing.comparePerMeal);
      compareElement.hidden = false;
    } else {
      compareElement.hidden = true;
    }
  }

  updateMealFilterOptionPrices(selectedPortion) {
    this.querySelectorAll('[js-meal-plan-builder="desktop-filter-option"][data-control="meals"]').forEach((optionElement) => {
      const amount = Number(optionElement.dataset.value);
      const baseElement = optionElement.querySelector('[js-meal-plan-builder="desktop-meal-price-base"]');
      const compareElement = optionElement.querySelector('[js-meal-plan-builder="desktop-meal-price-compare"]');

      this.applyPerMealPricingToElements({
        amount,
        portion: selectedPortion,
        baseElement,
        compareElement
      });
    });

    this.querySelectorAll('[js-meal-plan-builder="mobile-filter-option"][data-control="meals"]').forEach((optionElement) => {
      const amount = Number(optionElement.dataset.value);
      const baseElement = optionElement.querySelector('[js-meal-plan-builder="mobile-meal-price-base"]');
      const compareElement = optionElement.querySelector('[js-meal-plan-builder="mobile-meal-price-compare"]');

      this.applyPerMealPricingToElements({
        amount,
        portion: selectedPortion,
        baseElement,
        compareElement
      });
    });
  }

  applyPerBreakfastPricingToElements({ amount, baseElement, compareElement }) {
    if (!baseElement) {
      return;
    }

    const pricing = this.getBreakfastOptionPricing(amount);

    if (!pricing) {
      baseElement.textContent = '';

      if (compareElement) {
        compareElement.hidden = true;
      }

      return;
    }

    const discountRate = this.getActiveStep1DiscountRate();
    const hasDiscount = discountRate > 0;

    if (hasDiscount) {
      const discountedPerBreakfast = Math.max(pricing.basePerMeal * (1 - discountRate), 0);
      baseElement.textContent = `${this.formatMoney(discountedPerBreakfast)}/breakfast`;

      if (compareElement) {
        compareElement.textContent = this.formatMoney(pricing.basePerMeal);
        compareElement.hidden = false;
      }

      return;
    }

    baseElement.textContent = `${this.formatMoney(pricing.basePerMeal)}/breakfast`;

    if (!compareElement) {
      return;
    }

    if (pricing.comparePerMeal > 0) {
      compareElement.textContent = this.formatMoney(pricing.comparePerMeal);
      compareElement.hidden = false;
    } else {
      compareElement.hidden = true;
    }
  }

  updateBreakfastOptionPrices() {
    //filter dropdown
    this.querySelectorAll('[js-meal-plan-builder="desktop-filter-option"][data-control="breakfast"]').forEach((optionElement) => {
      const amount = Number(optionElement.dataset.value);
      const baseElement = optionElement.querySelector('[js-meal-plan-builder="desktop-breakfast-price-base"]');
      const compareElement = optionElement.querySelector('[js-meal-plan-builder="desktop-breakfast-price-compare"]');

      this.applyPerBreakfastPricingToElements({ amount, baseElement, compareElement });
    });

    this.querySelectorAll('[js-meal-plan-builder="mobile-filter-option"][data-control="breakfast"]').forEach((optionElement) => {
      const amount = Number(optionElement.dataset.value);
      const baseElement = optionElement.querySelector('[js-meal-plan-builder="mobile-breakfast-price-base"]');
      const compareElement = optionElement.querySelector('[js-meal-plan-builder="mobile-breakfast-price-compare"]');

      this.applyPerBreakfastPricingToElements({ amount, baseElement, compareElement });
    });
    
    //buttons on the page
    this.querySelectorAll('[js-meal-plan-builder="breakfast-buttons-prices-option"][data-control="breakfast"]').forEach((optionElement) => {
      const amount = Number(optionElement.dataset.value);
      const baseElement = optionElement.querySelector('[js-meal-plan-builder="breakfast-buttons-price-base"]');
      const compareElement = optionElement.querySelector('[js-meal-plan-builder="breakfast-buttons-price-compare"]');

      this.applyPerBreakfastPricingToElements({ amount, baseElement, compareElement });
    });


  }

  updateShotPackOptionPrices() {
    const pricing = this.getPerShotPricing();

    this.querySelectorAll('[js-meal-plan-builder="shot-pack-price-option"]').forEach((optionElement) => {
      const baseElement = optionElement.querySelector('[js-meal-plan-builder="shot-pack-price-base"]');
      const compareElement = optionElement.querySelector('[js-meal-plan-builder="shot-pack-price-compare"]');

      if (!baseElement) {
        return;
      }

      if (!pricing) {
        baseElement.textContent = '';

        if (compareElement) {
          compareElement.hidden = true;
        }

        return;
      }

      baseElement.textContent = `${this.formatMoney(pricing.finalPerShot)}/shot`;

      if (compareElement) {
        compareElement.textContent = `(${this.formatMoney(pricing.basePerShot)})`;
        compareElement.hidden = !pricing.showCompare;
      }
    });

    this.updateShotBadge(pricing);
  }

  // The "Try for" sticker over the shot card quotes the same per-shot price as the
  // pack options, so it is hidden whenever that price is unavailable.
  updateShotBadge(pricing = this.getPerShotPricing()) {
    const badgeElement = this.elements.shotBadge;
    const badgePriceElement = this.elements.shotBadgePrice;

    if (badgeElement) {
      badgeElement.hidden = !pricing;
    }

    if (badgePriceElement && pricing) {
      badgePriceElement.textContent = this.formatMoney(pricing.finalPerShot);
    }
  }

  updateStep1MealOptionPrices() {
    const selectedPortion = this.normalizePortionSize(this.state.step1.portion) || 'standard';

    this.querySelectorAll('.meal-plan-builder__swatch-item--meal').forEach((mealOptionElement) => {
      const mealsInput = mealOptionElement.querySelector('[js-meal-plan-builder="step1-meals"]');
      const basePriceElement = mealOptionElement.querySelector('[js-meal-plan-builder="step1-meal-price-base"]');
      const comparePriceElement = mealOptionElement.querySelector('[js-meal-plan-builder="step1-meal-price-compare"]');

      if (!mealsInput || !basePriceElement) {
        return;
      }

      this.applyPerMealPricingToElements({
        amount: Number(mealsInput.value),
        portion: selectedPortion,
        baseElement: basePriceElement,
        compareElement: comparePriceElement
      });
    });

    this.updateMealFilterOptionPrices(selectedPortion);
    this.updateStep1PortionAddonLabels();
  }

  getStep1PricingModel() {
    if (!isStep1Complete(this.state.step1)) {
      return null;
    }

    const mealsCount = Number(this.state.step1.mealsPerWeek) || 0;
    const selectedMealsPricing = this.getSelectedMealsOptionPricing();

    if (!selectedMealsPricing || mealsCount <= 0) {
      return null;
    }

    const baseSubtotal = selectedMealsPricing.basePerMeal * mealsCount;
    const compareSubtotal = selectedMealsPricing.comparePerMeal > 0
      ? selectedMealsPricing.comparePerMeal * mealsCount
      : 0;

    const discountRate = this.getActiveStep1DiscountRate();
    const hasDiscount = discountRate > 0;
    const originalMealsSubtotal = hasDiscount ? baseSubtotal : (compareSubtotal || baseSubtotal);
    const finalMealsSubtotal = hasDiscount ? Math.max(baseSubtotal * (1 - discountRate), 0) : baseSubtotal;
    const discountAmount = hasDiscount ? Math.max(originalMealsSubtotal - finalMealsSubtotal, 0) : 0;
    const discountAmountRounded = Math.round((discountAmount + Number.EPSILON) * 100) / 100;
    const originalPerMeal = originalMealsSubtotal / mealsCount;
    const finalPerMeal = finalMealsSubtotal / mealsCount;
    const deliveryPrice = Math.max(this.parsePriceNumber(this.dataset.step1DeliveryPrice), 0);
    const originalTotal = originalMealsSubtotal + deliveryPrice;
    const finalTotal = finalMealsSubtotal + deliveryPrice;

    return {
      mealsCount,
      hasDiscount,
      originalSubtotal: originalMealsSubtotal,
      finalSubtotal: finalMealsSubtotal,
      originalTotal,
      finalTotal,
      discountAmount: discountAmountRounded,
      originalPerMeal,
      finalPerMeal,
      showOldPerMeal: originalPerMeal > finalPerMeal + 0.0001,
      showOldSubtotal: originalMealsSubtotal > finalMealsSubtotal + 0.0001,
      showDiscountRow: hasDiscount && discountAmountRounded > 0,
      showOldTotal: originalTotal > finalTotal + 0.0001
    };
  }

  updateStep1PricingUI() {
    const pricingRoot = this.elements.step1Pricing;

    if (!pricingRoot) {
      return;
    }

    const isStep1Ready = isStep1Complete(this.state.step1);

    if (!isStep1Ready) {
      pricingRoot.hidden = true;
      return;
    }

    const pricing = this.getStep1PricingModel();

    if (!pricing) {
      pricingRoot.hidden = true;
      return;
    }

    pricingRoot.hidden = false;

    if (this.elements.step1PricePerMeal) {
      this.elements.step1PricePerMeal.hidden = !pricing.showDiscountRow;
    }

    if (this.elements.step1PricePerMealOld) {
      this.elements.step1PricePerMealOld.textContent = this.formatMoney(pricing.originalPerMeal);
      this.elements.step1PricePerMealOld.hidden = !pricing.showOldPerMeal;
    }

    if (this.elements.step1PricePerMealNew) {
      this.elements.step1PricePerMealNew.textContent = this.formatMoney(pricing.finalPerMeal);
    }

    if (this.elements.step1MainMealsCount) {
      this.elements.step1MainMealsCount.textContent = String(pricing.mealsCount);
    }

    if (this.elements.step1MainMealsTotalOld) {
      this.elements.step1MainMealsTotalOld.textContent = this.formatMoney(pricing.originalSubtotal);
      this.elements.step1MainMealsTotalOld.hidden = !pricing.showOldSubtotal;
    }

    if (this.elements.step1MainMealsTotal) {
      this.elements.step1MainMealsTotal.textContent = this.formatMoney(pricing.finalSubtotal);
    }

    if (this.elements.step1DiscountRow) {
      this.elements.step1DiscountRow.hidden = !pricing.showDiscountRow;
    }

    if (this.elements.step1DiscountBadge) {
      const offLabel = this.dataset.step1DiscountOffLabel || 'OFF';
      this.elements.step1DiscountBadge.textContent = `${this.formatMoney(pricing.discountAmount)} ${offLabel}`;
    }

    if (this.elements.step1TotalOld) {
      this.elements.step1TotalOld.textContent = this.formatMoney(pricing.originalTotal);
      this.elements.step1TotalOld.hidden = !pricing.showOldTotal;
    }

    if (this.elements.step1TotalNew) {
      this.elements.step1TotalNew.textContent = this.formatMoney(pricing.finalTotal);
    }
  }

  hasBreakfastSelection() {
    if (this.isBreakfastDisabledForDiet()) {
      return false;
    }

    return !this.state.step3.breakfastSkipped && this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] > 0;
  }

  syncBreakfastInputsFromState() {
    const breakfastLimit = this.state.step3.limits[COLLECTION_TYPES.BREAKFAST];

    this.elements.breakfastLimitInputs.forEach((input) => {
      input.checked = !this.state.step3.breakfastSkipped && Number(input.value) === breakfastLimit;
    });

    this.syncBuildControlsFromState();
  }

  applyStepUI() {
    this.elements.stepPanels.forEach((panel) => {
      const isActive = Number(panel.dataset.step) === this.state.step;

      panel.classList.toggle('is-active', isActive);
    });

    this.dataset.step = String(this.state.step);
    this.classList.toggle('is-build-step', this.state.step === STEP_IDS.BUILD);

    if (this.state.step !== STEP_IDS.BUILD) {
      this.closeMobileFilters();
    }
  }

  applyStageUI() {
    this.dataset.stage = this.state.stage;
    const breakfastDisabled = this.isBreakfastDisabledForDiet();

    this.elements.stagePanels.forEach((panel) => {
      panel.hidden = this.isStageNodeHidden(panel.dataset.stage, breakfastDisabled);
      panel.classList.toggle('is-active', panel.dataset.stage === this.state.stage);
    });

    this.elements.stageIndicators.forEach((indicator) => {
      indicator.hidden = this.isStageNodeHidden(indicator.dataset.stage, breakfastDisabled);
      indicator.classList.toggle('is-active', indicator.dataset.stage === this.state.stage);
    });

    const breakfastCollection = this.elements.collectionRoots[COLLECTION_TYPES.BREAKFAST];

    if (breakfastCollection) {
      const showBreakfastCollection =
        !breakfastDisabled &&
        !this.state.step3.breakfastSkipped &&
        this.state.step3.limits[COLLECTION_TYPES.BREAKFAST] > 0;
      breakfastCollection.classList.toggle('is-hidden', !showBreakfastCollection);
    }

    const drinksCollection = this.elements.collectionRoots[COLLECTION_TYPES.DRINKS];

    if (drinksCollection) {
      const shouldShowDrinksCollection = this.state.showDrinks;
      drinksCollection.classList.toggle('is-hidden', !shouldShowDrinksCollection);
    }

    this.elements.drinksDecisionButtons.forEach((button) => {
      button.classList.toggle('is-active', button.dataset.value === this.state.step3.drinksDecision);
    });

    this.syncFilterControlsUI();
  }

  isStageNodeHidden(stage, breakfastDisabled) {
    if (stage === STAGE_IDS.DRINKS) {
      return !this.state.showDrinks;
    }

    if (stage === STAGE_IDS.BREAKFAST) {
      return breakfastDisabled;
    }

    return false;
  }

  updateActionStates() {
    if (this.elements.selectPlanButton) {
      this.elements.selectPlanButton.disabled = !isStep1Complete(this.state.step1);
    }

    if (this.elements.summaryActionButton) {
      const nextLabel = this.elements.summaryActionButton.dataset.nextLabel || 'Next';
      const checkoutLabel = this.elements.summaryActionButton.dataset.checkoutLabel || 'Checkout';
      const isFinalStage = this.isFinalSummaryActionStage();
      const isDisabled = this.isSummaryActionDisabled();

      this.elements.summaryActionButton.disabled = isDisabled;
      this.elements.summaryActionButton.textContent = isFinalStage ? checkoutLabel : nextLabel;
    }
  }

  isFinalSummaryActionStage() {
    if (this.state.stage === STAGE_IDS.DRINKS) {
      return true;
    }

    if (this.state.showDrinks) {
      return false;
    }

    if (this.state.stage === STAGE_IDS.BREAKFAST) {
      return true;
    }

    return this.isBreakfastDisabledForDiet() && this.state.stage === STAGE_IDS.MEALS;
  }

  isSummaryActionDisabled() {
    const mealsComplete = this.isMealsComplete();
    const breakfastComplete = this.isBreakfastComplete();
    const drinksComplete = this.isDrinksComplete();
    const breakfastDisabled = this.isBreakfastDisabledForDiet();

    if (this.state.stage === STAGE_IDS.MEALS) {
      if (breakfastDisabled && !this.state.showDrinks) {
        return !mealsComplete || this.isSubmittingCheckout;
      }

      return !mealsComplete;
    }

    if (this.state.stage === STAGE_IDS.BREAKFAST) {
      return !breakfastComplete;
    }

    return !(drinksComplete && breakfastComplete && mealsComplete) || this.isSubmittingCheckout;
  }

  updateFooter() {
    const mealsSelected = this.getCollectionTotal(COLLECTION_TYPES.MEALS);
    const mealsLimit = this.state.step3.limits[COLLECTION_TYPES.MEALS];
    const breakfastSelected = this.getCollectionTotal(COLLECTION_TYPES.BREAKFAST);
    const breakfastLimit = this.state.step3.limits[COLLECTION_TYPES.BREAKFAST];
    const drinksSelected = this.getCollectionTotal(COLLECTION_TYPES.DRINKS);
    const drinksLimit = this.state.step3.limits[COLLECTION_TYPES.DRINKS];

    const addedLabel = this.dataset.summaryAddedLabel || 'added';
    const mealsLabel = this.dataset.summaryMealsLabel || 'Meals';
    const breakfastLabel = this.dataset.summaryBreakfastLabel || 'Breakfasts';
    const drinksLabel = this.dataset.summaryDrinksLabel || 'Drinks';
    const showBreakfastSummary =
      !this.isBreakfastDisabledForDiet() && !this.state.step3.breakfastSkipped && breakfastSelected > 0;
    const showDrinksSummary =
      this.state.showDrinks &&
      ((this.state.step3.drinksDecision === 'add' && drinksLimit > 0) || drinksSelected > 0);

    if (this.elements.footerSummary) {
      const summaryParts = [
        this.getSummaryLine({
          selected: mealsSelected,
          limit: mealsLimit,
          label: mealsLabel,
          addedLabel
        })
      ];

      if (showBreakfastSummary) {
        summaryParts.push(
          this.getSummaryLine({
            selected: breakfastSelected,
            limit: breakfastLimit,
            label: breakfastLabel,
            addedLabel
          })
        );
      }

      if (showDrinksSummary) {
        summaryParts.push(
          this.getSummaryLine({
            selected: drinksSelected,
            limit: drinksLimit,
            label: drinksLabel,
            addedLabel
          })
        );
      }

      this.elements.footerSummary.textContent = summaryParts.join(' | ');
    }
  }

  getSummaryLine({ selected, limit, label, addedLabel }) {
    const safeLimit = Math.max(0, Number(limit) || 0);
    const parsedSelected = Math.max(0, Number(selected) || 0);
    const safeSelected = safeLimit > 0 ? Math.min(parsedSelected, safeLimit) : parsedSelected;

    return `${safeSelected}/${safeLimit} ${label} ${addedLabel}`;
  }

  setContactError(message) {
    if (!this.elements.contactError) {
      return;
    }

    this.elements.contactError.textContent = message || '';
    this.elements.contactError.classList.toggle('is-hidden', !message);
  }

  normalizeMarketCode(value) {
    const code = String(value || '').trim().toUpperCase();

    if (!code) {
      return '';
    }

    if (['GB', 'UK'].includes(code)) {
      return 'UK';
    }

    if (['ROI', 'IE'].includes(code)) {
      return 'IE';
    }

    return code;
  }

  shouldRedirectToMarketSite(storeMarket, addressMarket) {
    if (!addressMarket || !MARKET_CONFIG[addressMarket]?.url) {
      return false;
    }

    // The UK storefront (uk.simmereats.com) serves Northern Ireland, so NI
    // addresses are accepted there. Any other UK-side address (Great Britain)
    // is redirected to the dedicated UK site.
    if (storeMarket === 'UK') {
      return addressMarket !== 'NI';
    }

    return addressMarket !== storeMarket;
  }

  getStoreMarketCode() {
    return this.normalizeMarketCode(this.dataset.market) || DEFAULT_STORE_MARKET;
  }

  // Updates error message and continue button text
  showMarketMismatch(addressMarketCode, storeMarketCode) {
    const addressMarket = MARKET_CONFIG[addressMarketCode];
    //TODO://remove check after uk migration
    const storeMarket = storeMarketCode === 'UK' ? MARKET_CONFIG['NI'] : MARKET_CONFIG[storeMarketCode];

    if (!addressMarket?.url) {
      this.setContactError("Sorry, we don't currently deliver to this postcode.");
      return;
    }

    const storeMarketName = storeMarket?.fullName || 'current';

    this.setContactError(
      `It appears that you're in ${addressMarket.fullName}, but you're currently on our ${storeMarketName} website.\nClick below to redirect for the best service.`
    );

    this.pendingMarketRedirect = addressMarket.url;

    const submitButton = this.elements.contactSubmit;

    if (submitButton) {
      if (this.contactSubmitDefaultText === null) {
        this.contactSubmitDefaultText = submitButton.textContent.trim();
      }

      submitButton.textContent = `Continue to ${addressMarket.shortName} site`;
      submitButton.classList.add('is-market-redirect');
    }
  }

  clearMarketMismatch() {
    if (!this.pendingMarketRedirect) {
      return;
    }

    this.pendingMarketRedirect = null;

    const submitButton = this.elements.contactSubmit;

    if (submitButton) {
      submitButton.classList.remove('is-market-redirect');

      if (this.contactSubmitDefaultText !== null) {
        submitButton.textContent = this.contactSubmitDefaultText;
      }
    }

    this.setContactError('');
  }

  setGlobalError(message) {
    if (!this.elements.globalError) {
      return;
    }

    this.elements.globalError.textContent = message || '';
    this.elements.globalError.classList.toggle('is-hidden', !message);
  }

  getCookieValue(name) {
    if (!name || typeof document === 'undefined') {
      return '';
    }

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length !== 2) {
      return '';
    }

    const cookieValue = parts.pop()?.split(';').shift() || '';

    return decodeURIComponent(cookieValue).trim();
  }

  getDiscountCode() {
    return this.getCookieValue('discount_code') || this.getCookieValue('discount') || '';
  }

  getDiscountCodeFromUrlParam() {
    if (typeof window === 'undefined') {
      return '';
    }

    const queryString = String(window.location?.search || '').trim();

    if (!queryString) {
      return '';
    }

    const searchParams = new URLSearchParams(queryString);
    return String(searchParams.get('rc_code') || '').trim();
  }

  getDefaultMarketDiscountCode() {
    return this.decodeHtmlEntities(this.dataset.defaultDiscountCode || '').trim();
  }

  setDiscountCodeCookie(discountCode, daysToExpire = 30) {
    const normalizedCode = String(discountCode || '').trim();

    if (!normalizedCode || typeof document === 'undefined') {
      return;
    }

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + Math.max(1, Number(daysToExpire) || 1));
    const encodedCode = encodeURIComponent(normalizedCode);
    const expiresDirective = `expires=${expiresAt.toUTCString()}`;
    document.cookie = `discount_code=${encodedCode}; ${expiresDirective}; path=/; SameSite=Lax`;
  }

  buildCheckoutUrl() {
    const rootPath = window.Shopify?.routes?.root || '/';
    const normalizedRoot = String(rootPath).endsWith('/') ? String(rootPath) : `${rootPath}/`;
    const checkoutUrl = new URL(`${normalizedRoot}checkout`, window.location.origin);

    const locale = String(window.Shopify?.locale || '').trim();
    const market = String(window.Shopify?.country || '').trim();
    const discountCode = this.getDiscountCode();
    const email = loadEmail();
    const postcode = loadPostcode();

    if (locale) {
      checkoutUrl.searchParams.set('locale', locale);
    }

    if (market) {
      checkoutUrl.searchParams.set('country', market);
      checkoutUrl.searchParams.set('checkout[shipping_address][country]', market);
      checkoutUrl.searchParams.set('checkout[shipping_address][country_code]', market);
    }

    if (discountCode) {
      checkoutUrl.searchParams.set('discount', discountCode);
    }

    if (email) {
      checkoutUrl.searchParams.set('checkout[email]', email);
    }

    if (postcode) {
      checkoutUrl.searchParams.set('checkout[shipping_address][zip]', postcode);
    }

    return checkoutUrl.toString();
  }

  scrollToTop() {
    if (typeof window === 'undefined' || typeof window.scrollTo !== 'function') {
      return;
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  refreshAfterStep3Change(shouldPersist = true) {
    this.applyStageUI();
    this.updateActionStates();
    this.updateFooter();

    if (shouldPersist) {
      saveStep3({
        ...this.state.step3,
        stage: this.state.stage
      });
    }
  }
}

customElements.define('meal-plan-builder', MealPlanBuilder);
